import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AppAdmin.css';
import { VEHICLE_COLORS, vehicleColorLabel } from './constants/vehicleColors';
import { 
  Users, TrendingUp, Car, MapPin, DollarSign, Calendar, 
  Filter, CheckCircle, XCircle, 
  AlertCircle, Clock, Eye, BarChart3, Edit2, Save,
  Menu, X, Bell, Settings, LogOut, FileText,
  User, Key, Crown, GraduationCap, CreditCard, Layers, Star,
  Shield, Search, Wallet, UserCheck, Image, MessageSquare,
  // Amélioration 1 — sidebar collapsible
  ChevronDown, ChevronRight,
  ShieldCheck, Truck, Navigation, Route, ShoppingBag, Store, Bike, Map, Bed,
  BarChart2, LayoutDashboard, Mail, LifeBuoy, Globe, Package, Ticket,
  // Amélioration 5 — CSV export
  Download,
  // Onglet Applications (releases APK)
  Smartphone,
  // Mode maintenance
  Wrench,
  Bug,
  // Salles & espaces événementiels
  PartyPopper,
  // Boîte de réception WhatsApp
  MessageCircle,
} from 'lucide-react';
import authServiceAdmin from './authServiceAdmin';
import apiService from './apiService';
import { connectLive, disconnectLive } from './liveRefresh';

// Ville de travail choisie à l'inscription : slug → nom lisible (liste publique, cache au niveau du module)
const CITY_NAMES = {};
const loadCityNames = () => apiService.get('/cities').then(r => { (r?.cities || []).forEach(c => { CITY_NAMES[c.slug] = c.name; }); }).catch(() => {});
// Catégories chauffeur : l'admin fait foi, AUCUNE implication vers le bas (premium seul = premium uniquement).
// Seule règle automatique : pas de trou dans l'échelle (premium + standard ⇒ confort). Même règle côté serveur.
const fillCategoryGaps = (cats) => {
  const out = [...new Set(cats || [])];
  if (out.includes('premium') && out.includes('standard') && !out.includes('confort')) out.push('confort');
  return out;
};
const cityName = (slug) => (slug ? (CITY_NAMES[slug] || slug.charAt(0).toUpperCase() + slug.slice(1)) : '—');
import { exportToCSV } from './utils/adminHelpers';
import { getPushPermission, requestPushPermission, showDesktopNotification } from './utils/pushNotifications';
import { AdminContext } from './context/AdminContext';
import ReviewsPage from './pages/ReviewsPage';
import TargetReviews from './components/TargetReviews';
import MessagesPage from './pages/MessagesPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import SettingsPage from './pages/SettingsPage';
import DashboardPage from './pages/DashboardPage';
import BeautyBookingsPage from './pages/beauty/BeautyBookingsPage';
import ClientValidationPage from './pages/ClientValidationPage';
import DriverValidationPage from './pages/DriverValidationPage';
import DriverPromosPage from './pages/DriverPromosPage';
import DriversPage from './pages/DriversPage';
import StopsPage from './pages/StopsPage';
import LinesPage from './pages/LinesPage';
import BalancesTransfersPage from './pages/BalancesTransfersPage';
import ClientBalancePage from './pages/ClientBalancePage';
import PlatformSettingsPage from './pages/PlatformSettingsPage';
import SecurityCenterPage from './pages/SecurityCenterPage';
import MaintenancePage from './pages/MaintenancePage';
import ErrorsPage from './pages/ErrorsPage';
import WhatsAppPage from './pages/WhatsAppPage';
import AppReleasesPage from './pages/AppReleasesPage';
import SubscribersPage from './pages/SubscribersPage';
import RevenuePage from './pages/RevenuePage';
import FinancePilotagePage from './pages/finance/FinancePilotagePage';
import TaxiRidesPage from './pages/TaxiRidesPage';
import ActiveRidesPage from './pages/ActiveRidesPage';
import OnlineDriversPage from './pages/OnlineDriversPage';
import OpenBusinessesPage from './pages/OpenBusinessesPage';
import PlansPage from './pages/PlansPage';
import PaymentsPage from './pages/PaymentsPage';
import EventsAdminPage from './pages/EventsAdminPage';

// Badge(s) de méthode de connexion d'un utilisateur (Google / Apple / Classique).
// Suppression définitive (sécurité 2026-11-02) : le serveur exige quoi faire des paiements Mobile Money
// (pièces comptables). Saisie explicite — tout autre texte ou Annuler interrompt la suppression.
function askPaymentsChoice(who) {
  const v = window.prompt(`Paiements Mobile Money de ${who} :\n\n• Tapez ANONYMISER pour les garder sans données personnelles (montant, date, statut conservés — recommandé)\n• Tapez EFFACER pour les supprimer définitivement\n\nAnnuler = ne rien supprimer.`, 'ANONYMISER');
  if (v == null) return null;
  const t = String(v).trim().toUpperCase();
  if (t === 'ANONYMISER') return 'anonymize';
  if (t === 'EFFACER') return 'delete';
  window.alert('Suppression annulée : réponse non reconnue.');
  return null;
}

function authMethodBadges(user) {
  const providers = (user && user.auth_providers) || [];
  const chips = [];
  if (providers.some(p => p.provider === 'google')) chips.push(['Google', '#ea4335']);
  if (providers.some(p => p.provider === 'apple')) chips.push(['Apple', '#12151A']);
  if (!chips.length) chips.push(['Classique', '#5C636E']);
  return chips.map(([label, color]) => (
    <span key={label} style={{ background: color + '18', color, padding: '2px 10px', borderRadius: 999, fontSize: '0.8rem', fontWeight: 700, marginRight: 6 }}>{label}</span>
  ));
}

// ========== LODGING ADMIN PAGE ==========
import LodgingModerationPage from './pages/lodging/LodgingModerationPage';
import VenuesAdminPage from './pages/venues/VenuesAdminPage';
import VenueBookingsAdminPage from './pages/venues/VenueBookingsAdminPage';
// ========== ADMINISTRATION (comptes & audit) ==========
import AdminAccountsPage from './pages/admin/AdminAccountsPage';
import AdminAuditLogPage from './pages/admin/AdminAuditLogPage';

// ========== DELIVERY ADMIN PAGES ==========
import DeliverySuppliersPage from './pages/delivery/DeliverySuppliersPage';
import StoreTemplatesPage from './pages/delivery/StoreTemplatesPage';
import SupplierDetailPage from './pages/delivery/SupplierDetailPage';
import DeliveryOrdersPage from './pages/delivery/DeliveryOrdersPage';
import DeliveryZonesPage from './pages/delivery/DeliveryZonesPage';
import DeliveryDriversPage from './pages/delivery/DeliveryDriversPage';
import CategoryTemplatesPage from './pages/delivery/CategoryTemplatesPage';
import SupportPage from './pages/delivery/SupportPage';
import DeliveryStatsPage from './pages/delivery/DeliveryStatsPage';
import CitiesPage from './pages/delivery/CitiesPage';
import Img from './components/Img';

// Composants définis À L'INTÉRIEUR du composant parent (pages, modales) : leur « type » est
// une nouvelle fonction à chaque rendu du parent → React les démonte et les remonte, ce qui
// vide les formulaires en cours de saisie (ex. à chaque poll de 30 s). <Stable render={X} />
// garde un type constant (Stable) tout en exécutant la dernière version de X : l'état interne
// (useState) survit aux rendus du parent, les fermetures restent fraîches.
function Stable({ render, ...props }) { return render(props); }


// Pièce d'identité / permis : quand le chauffeur a REMPLACÉ une pièce, l'ancienne (validée) et la nouvelle
// (en attente) sont montrées côte à côte pour l'analyse de validation ; clic = ouvrir en grand.
function DocumentCompare({ current, previous, replacedAt, alt }) {
  const open = (src) => window.open(src, '_blank', 'noopener');
  if (!previous) {
    return <Img src={current} alt={alt} className="doc-image document" style={{ cursor: 'zoom-in' }} onClick={() => open(current)} />;
  }
  const when = replacedAt ? new Date(replacedAt).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : null;
  const tag = (txt, bg, fg) => <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: bg, color: fg, marginBottom: 4 }}>{txt}</span>;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div>
          {tag('Ancienne (validée)', '#F4F5F7', '#5C636E')}
          <Img src={previous} alt={`${alt} — ancienne`} className="doc-image document" style={{ cursor: 'zoom-in', opacity: 0.9 }} onClick={() => open(previous)} />
        </div>
        <div>
          {tag('Nouvelle (à valider)', '#FFF7ED', '#9A3412')}
          <Img src={current} alt={`${alt} — nouvelle`} className="doc-image document" style={{ cursor: 'zoom-in', borderColor: '#FDBA74' }} onClick={() => open(current)} />
        </div>
      </div>
      {when && <div style={{ fontSize: 12, color: '#9A3412', marginTop: 4 }}>Remplacée le {when} — comparez les deux avant d'approuver.</div>}
    </div>
  );
}

// ==================== CONSTANTS ====================
// Plans de prix alignés avec l'API backend
const PLAN_DETAILS = {
  basic: {
    name: 'BASIQUE',
    description: 'Trajets fixes partagés',
    icon: MapPin,
    color: '#10b981',
    features: ['Trajets limités', 'Partagé', 'Économique']
  },
  'private-basic': {
    name: 'PRIVÉ BASIQUE',
    description: 'Trajets fixes privatisés',
    icon: Car,
    color: '#FF7800',
    features: ['Trajets limités', 'Privatisé', 'Confortable']
  },
  standard: {
    name: 'STANDARD',
    description: 'Tous trajets partagés',
    icon: Users,
    color: '#FFB900',
    features: ['Tous les trajets', 'Partagé', 'Liberté maximale']
  },
  premium: {
    name: 'PREMIUM',
    description: 'Tous trajets privatisés',
    icon: Crown,
    color: '#FF7800',
    features: ['Tous les trajets', 'Privatisé', 'Intimité garantie']
  },
  university: {
    name: 'UNIVERSITÉ',
    description: 'Campus - 05h30 à 19h00',
    icon: GraduationCap,
    color: '#06b6d4',
    features: ['Campus uniquement', 'Horaires fixes', 'Tarif étudiant']
  },
  elite: {
    name: 'ÉLITE',
    description: 'Taxi privé illimité',
    icon: Crown,
    color: '#dc2626',
    features: ['Illimité', '07:00 - 19:00', 'Préférence VIP']
  }
};

// ==================== SUBSCRIPTIONS PAGE COMPONENT (EXTRACTED) ====================
// This is a SEPARATE component to prevent it from being recreated on every parent render

// Politique d'accès du menu/pages par service — miroir de backend/middleware/adminAccess.js.
// Une page absente d'ici = accessible à tout admin connecté. superadmin voit tout.
const PAGE_ROLES = {
  validation: ['validation'],
  'client-validation': ['validation'],
  drivers: ['validation'],
  'lodging-moderation': ['validation'],
  'lodging-reviews': ['validation'],
  venues: ['validation'],
  'venue-bookings': ['validation'],
  abonnes: ['ops_transport'],
  taxis: ['ops_transport'],
  'active-rides': ['ops_transport'],            // page « Courses actives » (carte du dashboard)
  'online-drivers': ['ops_transport', 'validation'], // page « Chauffeurs en ligne » (carte du dashboard)
  stops: ['ops_transport'],
  routes: ['ops_transport'],
  payments: ['finance'],
  revenue: ['finance'],
  'finance-pilotage': ['finance'],
  soldes: ['finance'],
  'client-soldes': ['finance'],
  'delivery-orders': ['ops_delivery'],
  'beauty-bookings': ['ops_delivery'],
  'delivery-drivers': ['ops_delivery'],
  'delivery-category-templates': ['ops_delivery'],
  'delivery-zones': ['ops_delivery'],
  'delivery-stats': ['ops_delivery'],
  'delivery-suppliers': ['ops_delivery', 'validation'],
  'open-businesses': ['ops_delivery', 'validation'], // page « Business ouverts » (carte du dashboard)
  'driver-promos': ['finance'],
  events: ['ops_delivery', 'support'],
  'store-templates': ['ops_delivery', 'support'],
  ratings: ['support'],
  messages: ['support'],
  support: ['support'],
  subscriptions: ['superadmin'],      // Plans / tarifs
  security: ['support', 'validation'],
  'platform-settings': ['superadmin'],
  'app-releases': ['it'],
  maintenance: ['it'],
  errors: ['it'],
  whatsapp: ['support'],            // boîte de réception WhatsApp (2026-10-30)
  cities: ['superadmin'],
  admins: ['superadmin'],
  'audit-logs': ['it'],
};

// Associe un type de notification à l'onglet (page) concerné → badge « bulle + chiffre ».
// SOURCE UNIQUE : sert aux pastilles du menu ET au clic dans le panneau des
// notifications. Tout nouveau type de notification admin doit être ajouté ici.
const NOTIF_PAGE_MAP = {
  // Sécurité
  safety_sos: 'security', safety_watch: 'security',
  // Chauffeurs
  driver_registration: 'validation', driver_resubmit: 'validation', driver_vehicle_update: 'validation',
  driver_pending: 'validation',
  driver_approved: 'drivers', driver_rejected: 'drivers', driver_suspended: 'drivers',
  // Clients
  identity_verification: 'client-validation',
  bad_rating_client: 'ratings', bad_rating_driver: 'ratings', bad_rating_supplier: 'ratings', driver_rating_watch: 'drivers',
  // Livraison & commerces
  supplier_registration: 'delivery-suppliers',
  delivery_order: 'delivery-orders', delivery_blocked: 'delivery-orders',
  beauty_booking: 'beauty-bookings',
  lodging_booking: 'lodging-moderation',
  venue_pending: 'venues', venue_booking: 'venue-bookings', venue_supplier_cancel: 'venue-bookings',
  // Billetterie
  event_pending: 'events', event_cancel_request: 'events', event_organizer: 'events',
  // Support
  support_ticket: 'support',
  // Finance
  payment_received: 'payments', balance_credited: 'payments', balance_failed: 'payments',
  topup: 'payments', refund: 'payments',
  subscription_created: 'abonnes', subscription_expired: 'abonnes',
};

// Extrait l'id de page depuis le hash de l'URL (#/orders -> 'orders').
function getPageFromHash() {
  try { return (window.location.hash || '').replace(/^#\/?/, '').trim() || null; }
  catch { return null; }
}

export default function TaxiAdminDashboard() {
  const [currentPage, setCurrentPage] = useState('login');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  // Rôles de service de l'admin connecté (superadmin = accès total).
  // Une session "legacy" (currentAdmin sans tableau `roles`, ex. connecté avant
  // la migration multi-services) est traitée comme superadmin → accès total.
  const adminRoles = currentAdmin?.roles;
  const isSuperAdmin = !Array.isArray(adminRoles) || adminRoles.includes('superadmin');
  // Actions financières (solde chauffeur, déblocage, ajustement, payout) réservées à Finance/Super Admin.
  const canFinance = isSuperAdmin || (Array.isArray(adminRoles) && adminRoles.includes('finance'));
  const canAccessPage = (page) => {
    if (isSuperAdmin) return true;
    const req = PAGE_ROLES[page];
    if (!req) return true; // page sans restriction de service
    return req.some(r => adminRoles.includes(r));
  };
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientEdit, setSelectedClientEdit] = useState(false); // ouvrir le détail user directement en édition
  const [viewingClientTrips, setViewingClientTrips] = useState(false);
  const [viewingClientUserId, setViewingClientUserId] = useState(null);
  const [viewingClientName, setViewingClientName] = useState(null);

  // Ouvre le détail (éditable) d'un utilisateur par son id — ex. depuis un fournisseur.
  const openUserEditor = async (ownerId) => {
    if (!ownerId) { alert('Propriétaire introuvable pour ce fournisseur'); return; }
    try {
      const res = await apiService.get(`/admin/users/${ownerId}`, { userType: 'admin' });
      if (res?.success && res.user) {
        // Le modal lit les infos via subscription.user.* → on enveloppe l'utilisateur.
        setSelectedClient({ user: res.user });
        setSelectedClientEdit(true);
        setCurrentPage('abonnes');
      } else {
        alert(res?.error || 'Utilisateur introuvable');
      }
    } catch (e) {
      alert(e?.error || e?.message || "Impossible de charger l'utilisateur");
    }
  };
  const [editingPrices, setEditingPrices] = useState(false);
  const [planPrices, setPlanPrices] = useState({});
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  // Notifications push (bureau) : état de la permission navigateur + dernier id notifié.
  const [pushPermission, setPushPermission] = useState(getPushPermission());
  const [pushDismissed, setPushDismissed] = useState(false);
  const lastNotifiedIdRef = useRef(null);
  const notifPrimedRef = useRef(false);
  const notifSigRef = useRef(''); // signature (ids + lu) : pas de re-rendu du dashboard toutes les 30 s si rien n'a changé
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('approved');
  const [searchText, setSearchText] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [showEditSubscriptionModal, setShowEditSubscriptionModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [showEditDriverModal, setShowEditDriverModal] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editDriverFormData, setEditDriverFormData] = useState({
    driver_name: '',
    driver_phone: '',
    driver_email: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_color: '',
    vehicle_plate: '',
    vehicle_year: '',
    license_number: '',
    id_number: '',
    driver_categories: ['standard'],
    approved_roles: ['driver']
  });
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  // V4 sécurité : « Déconnecter tous les appareils » (null | 'confirm' | 'loading' | { ok, text })
  const [revokeSessionsState, setRevokeSessionsState] = useState(null);
  // Suspension / bannissement d'un client depuis « Vérifier l'abonnement du client » (2026-10-29)
  const [accountAction, setAccountAction] = useState(null); // { status: 'suspended'|'banned'|'active', reason, busy, error, done }
  useEffect(() => { if (showEditSubscriptionModal) setAccountAction(null); }, [showEditSubscriptionModal]);
  const [editFormData, setEditFormData] = useState({
    plan_type: '',
    plan_name: '',
    valid_until: '',
    status: 'active',
    client_name: '',
    client_phone: '',
    client_email: ''
  });
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [routeForm, setRouteForm] = useState({ departure_id: '', arrival_id: '', bidirectional: true, active: true });
  const [routes, setRoutes] = useState([]);
  const [stops, setStops] = useState([]);
  const [showStopModal, setShowStopModal] = useState(false);
  const [selectedStop, setSelectedStop] = useState(null);
  const [stopForm, setStopForm] = useState({ name: '', description: '' });
  const [loadingStops, setLoadingStops] = useState(false);
  const [loadingRoutes, setLoadingRoutes] = useState(false);

  // État des sections collapsibles de la sidebar (true = ouvert)
  const [sidebarOpen, setSidebarOpen] = useState({ lodging: false,
    users: true,
    finances: true,
    transport: true,
    delivery: false,
    platform: true,
  });

  // États pour la page Revenu
  const [revenueSummary, setRevenueSummary] = useState({ totalRevenue: 0, activeSubscriptions: 0, expiredSubscriptions: 0, totalSubscriptions: 0, averagePrice: 0 });
  const [revenueByPlan, setRevenueByPlan] = useState([]);
  const [revenueStats, setRevenueStats] = useState([]);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [revenueStartDate, setRevenueStartDate] = useState('');
  const [revenueEndDate, setRevenueEndDate] = useState('');
  const [revenueGroupBy, setRevenueGroupBy] = useState('day');
  const [revenueFilterPreset, setRevenueFilterPreset] = useState('30days'); // État du filtre sélectionné
  const [revenueLocalStartDate, setRevenueLocalStartDate] = useState('');
  const [revenueLocalEndDate, setRevenueLocalEndDate] = useState('');
  const hasLoadedRevenueRef = useRef(false);

  // Vérifier si admin est déjà connecté
  useEffect(() => {
    if (authServiceAdmin.isAuthenticated()) {
      const admin = authServiceAdmin.getCurrentUser();
      setCurrentAdmin(admin);
      setIsAuthenticated(true);
      // Restaurer la page depuis l'URL (hash) pour que le refresh reste sur place.
      // La garde d'accès (plus bas) renverra au dashboard si la page n'est pas autorisée.
      setCurrentPage(getPageFromHash() || 'dashboard');
      // Charger les données du backend
      loadDriversFromAPI();
      loadSubscriptionsFromAPI();
      loadNotificationsFromAPI();
      // Charger les revenus des 30 derniers jours pour affichage Dashboard
      loadRevenueDashboard();
      // Charger les arrêts et lignes
      loadStopsFromAPI();
      loadRoutesFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rafraîchissement périodique des notifications (le badge se met à jour sans action de
  // l'admin — les nouvelles demandes de validation chauffeur/fournisseur apparaissent seules).
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(() => { loadNotificationsFromAPI(); }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Temps réel (2026-10-30) : le serveur pousse chaque notification admin et chaque erreur
  // nouvelle → cloche et badges immédiats, liste des chauffeurs rechargée sans spinner ; les pages
  // de liste écoutent le même événement (useLiveRefresh). Le polling ci-dessus reste en secours.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const token = apiService.getToken('admin');
    if (!token) return undefined;
    connectLive(token, (evt) => {
      loadNotificationsFromAPI();
      if (/^driver_/.test(evt?.type || '')) loadDriversFromAPI({ silent: true });
    });
    return () => disconnectLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Garde d'accès : si l'admin n'a pas le droit pour la page courante → dashboard
  useEffect(() => {
    if (isAuthenticated && currentAdmin && currentPage !== 'login' && !canAccessPage(currentPage)) {
      setCurrentPage('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, currentAdmin, isAuthenticated]);

  // ── Navigation liée à l'URL (hash) + historique navigateur ──────────────────
  // (1) le refresh reste sur la page courante (page restaurée depuis le hash) ;
  // (2) le bouton « retour » du navigateur revient à la page précédente de l'app
  //     au lieu de quitter le site (chaque navigation pousse une entrée d'historique).
  const skipHashPushRef = useRef(false);
  useEffect(() => {
    const onPop = () => {
      skipHashPushRef.current = true; // vient de l'historique → ne pas re-pousser
      setCurrentPage(getPageFromHash() || 'dashboard');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (!isAuthenticated || currentPage === 'login') return;
    if (skipHashPushRef.current) { skipHashPushRef.current = false; return; }
    const target = `#/${currentPage}`;
    if (window.location.hash !== target) {
      window.history.pushState({ page: currentPage }, '', target);
    }
  }, [currentPage, isAuthenticated]);

  // Recharger les données après un login frais (nouveau domaine ou token expiré)
  useEffect(() => {
    if (isAuthenticated && currentAdmin && drivers.length === 0 && subscriptions.length === 0) {
      loadDriversFromAPI();
      loadSubscriptionsFromAPI();
      loadNotificationsFromAPI();
      loadRevenueDashboard();
      loadStopsFromAPI();
      loadRoutesFromAPI();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, currentAdmin]);

  // Charger les chauffeurs depuis l'API
  const loadDriversFromAPI = async (opts = {}) => {
    if (!opts.silent) setLoadingDrivers(true); // silent : rechargement temps réel, sans spinner
    try {
      // Charger les chauffeurs traités et pending EN PARALLÈLE (pas séquentiellement).
      // Sans paramètre « status », /admin/drivers renvoie approuvés + suspendus + rejetés (tout sauf
      // pending et deleted) : avec « ?status=approved » les suspendus et rejetés n'arrivaient jamais
      // et les filtres « Suspendus » / « Rejetés » de l'onglet Chauffeurs restaient vides (2026-10-31).
      const [approvedResponse, pendingResponse] = await Promise.all([
        apiService.get('/admin/drivers', { userType: 'admin' }),
        apiService.get('/admin/drivers/pending', { userType: 'admin' })
      ]);
      
      // Combiner les deux listes pour avoir tous les chauffeurs
      let allDrivers = [];
      if (approvedResponse.success && approvedResponse.drivers) {
        allDrivers = allDrivers.concat(approvedResponse.drivers);
      }
      if (pendingResponse.success && pendingResponse.drivers) {
        allDrivers = allDrivers.concat(pendingResponse.drivers);
      }
      
      if (allDrivers.length > 0) {
        setDrivers(allDrivers);
        console.log(`✅ Chargé ${allDrivers.length} chauffeurs (${approvedResponse.drivers?.length || 0} traités : approuvés/suspendus/rejetés, ${pendingResponse.drivers?.length || 0} pending)`);
      }
    } catch (error) {
      console.error('Erreur chargement chauffeurs:', error);
      setErrorMessage('Impossible de charger les chauffeurs');
    } finally {
      setLoadingDrivers(false);
    }
  };

  // Charger les abonnements depuis l'API
  const loadSubscriptionsFromAPI = async () => {
    setLoadingSubscriptions(true);
    try {
      const response = await apiService.get('/admin/subscriptions', { userType: 'admin' });
      if (response.success && response.subscriptions) {
        setSubscriptions(response.subscriptions);
      } else if (response.subscriptions && Array.isArray(response.subscriptions)) {
        setSubscriptions(response.subscriptions);
      }
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
      setErrorMessage('Impossible de charger les abonnements');
    } finally {
      setLoadingSubscriptions(false);
    }
  };

  // Demander l'autorisation d'afficher les notifications push (bureau).
  const enablePush = async () => {
    const result = await requestPushPermission();
    setPushPermission(result);
    if (result === 'granted') {
      showDesktopNotification('Mbiyo Admin', { body: 'Notifications push activées ✓' });
    }
  };

  // Charger les notifications depuis l'API
  const loadNotificationsFromAPI = async () => {
    try {
      const response = await apiService.get('/admin/notifications?limit=50', { userType: 'admin' });
      if (response.success && response.notifications) {
        // Formater les notifications pour affichage
        const formattedNotifications = response.notifications.map(notif => ({
          id: notif.notification_id,
          type: notif.type.includes('driver') ? 'driver' : notif.type.includes('payment') ? 'payment' : 'client',
          notifType: notif.type,
          message: notif.message,
          data: notif.data || {},
          read: notif.read,
          created_at: notif.created_at,
          time: getTimeAgo(notif.created_at)
        }));
        const sig = formattedNotifications.map(n => `${n.id}:${n.read ? 1 : 0}`).join(',');
        if (sig !== notifSigRef.current) {
          notifSigRef.current = sig;
          setNotifications(formattedNotifications);
        }
        // Notification bureau pour tout nouvel élément (on n'alerte pas au 1er chargement).
        const newest = formattedNotifications[0];
        if (newest) {
          if (!notifPrimedRef.current) {
            notifPrimedRef.current = true;
            lastNotifiedIdRef.current = newest.id;
          } else if (newest.id !== lastNotifiedIdRef.current) {
            lastNotifiedIdRef.current = newest.id;
            if (!newest.read) showDesktopNotification('Mbiyo Admin', { body: newest.message, tag: String(newest.id) });
          }
        }
        console.log(`✅ ${formattedNotifications.length} notifications chargées`);
      }
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
      // Ne pas afficher d'erreur, juste garder les notifications vides (sans re-rendu inutile)
      setNotifications(prev => (prev.length ? [] : prev));
    }
  };

  // Fonction utilitaire pour formatage temps
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  // Charger les données de revenu depuis l'API (avec paramètres optionnels)
  const loadRevenueFromAPI = useCallback(async (startDate = revenueStartDate, endDate = revenueEndDate, groupBy = revenueGroupBy) => {
    setLoadingRevenue(true);
    setErrorMessage('');
    try {
      // Construire les paramètres de requête
      const buildQueryParams = () => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return params.toString();
      };

      const dateQueryString = buildQueryParams();
      const statsQueryString = dateQueryString + (dateQueryString ? '&' : '') + 'groupBy=' + groupBy;

      console.log('📊 Chargement revenus avec:', { startDate, endDate, groupBy, dateQueryString, statsQueryString });

      // Charger toutes les données en parallèle
      const [summaryResponse, byPlanResponse, statsResponse] = await Promise.all([
        apiService.get(
          `/admin/revenue/summary${dateQueryString ? '?' + dateQueryString : ''}`,
          { userType: 'admin' }
        ),
        apiService.get(
          `/admin/revenue/by-plan${dateQueryString ? '?' + dateQueryString : ''}`,
          { userType: 'admin' }
        ),
        apiService.get(
          `/admin/revenue/statistics${statsQueryString ? '?' + statsQueryString : ''}`,
          { userType: 'admin' }
        )
      ]);

      console.log('📊 Réponses reçues:', { summaryResponse, byPlanResponse, statsResponse });

      // Mettre à jour tous les états en un seul batch
      if (summaryResponse.success && summaryResponse.summary) {
        setRevenueSummary(summaryResponse.summary);
      }
      if (byPlanResponse.success && byPlanResponse.data) {
        setRevenueByPlan(byPlanResponse.data);
      }
      if (statsResponse.success && statsResponse.data) {
        setRevenueStats(statsResponse.data);
      }

      console.log('✅ Revenus chargés avec succès');
    } catch (error) {
      console.error('❌ Erreur chargement revenus:', error);
      setErrorMessage('Impossible de charger les données de revenu');
    } finally {
      setLoadingRevenue(false);
    }
  }, [revenueStartDate, revenueEndDate, revenueGroupBy]);

  // Charger les arrêts/lignes quand on navigue vers ces pages
  useEffect(() => {
    if (currentPage === 'stops') {
      loadStopsFromAPI();
    } else if (currentPage === 'routes') {
      loadRoutesFromAPI();
      if (stops.length === 0) loadStopsFromAPI();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Charger les revenus au montage de la page Revenue (UNE SEULE FOIS)
  useEffect(() => {
    if (currentPage === 'revenue' && !hasLoadedRevenueRef.current) {
      hasLoadedRevenueRef.current = true;
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const endDateStr = today.toISOString().split('T')[0];
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      const startDateStr = start.toISOString().split('T')[0];
      setRevenueLocalStartDate(startDateStr);
      setRevenueLocalEndDate(endDateStr);
      setRevenueFilterPreset('30days');
      loadRevenueFromAPI(startDateStr, endDateStr, 'day');
    }
  }, [currentPage, loadRevenueFromAPI]);

  // Handlers pour la page Revenue (au niveau parent pour stabilité)
  const handleRevenuePresetFilter = useCallback((preset) => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const endDateStr = today.toISOString().split('T')[0];
    let startDateStr = '';
    if (preset === '30days') {
      const s = new Date(today); s.setDate(s.getDate() - 30);
      startDateStr = s.toISOString().split('T')[0];
    } else if (preset === '1year') {
      const s = new Date(today); s.setFullYear(s.getFullYear() - 1);
      startDateStr = s.toISOString().split('T')[0];
    }
    setRevenueFilterPreset(preset);
    setRevenueLocalStartDate(startDateStr);
    setRevenueLocalEndDate(endDateStr);
    setRevenueStartDate(startDateStr);
    setRevenueEndDate(endDateStr);
    loadRevenueFromAPI(startDateStr, endDateStr, revenueGroupBy);
  }, [loadRevenueFromAPI, revenueGroupBy]);

  const handleRevenueFilterApply = useCallback(() => {
    setRevenueStartDate(revenueLocalStartDate);
    setRevenueEndDate(revenueLocalEndDate);
    setRevenueFilterPreset('custom');
    loadRevenueFromAPI(revenueLocalStartDate, revenueLocalEndDate, revenueGroupBy);
  }, [loadRevenueFromAPI, revenueLocalStartDate, revenueLocalEndDate, revenueGroupBy]);

  const handleRevenueReset = useCallback(() => {
    handleRevenuePresetFilter('30days');
  }, [handleRevenuePresetFilter]);

  // Calculer le nombre de notifications non lues
  const unreadCount = notifications.filter(n => !n.read).length;

  // Charger les revenus depuis le début (sans filtres) pour Dashboard
  // Charger les revenus des 30 derniers jours pour le Dashboard (pour cohérence avec RevenuePage)
  const loadRevenueDashboard = async () => {
    try {
      // Calculer les dates pour 30 derniers jours
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      const endDateStr = today.toISOString().split('T')[0];
      
      const start = new Date(today);
      start.setDate(start.getDate() - 30);
      const startDateStr = start.toISOString().split('T')[0];
      
      console.log(`📊 Dashboard: Chargement revenus 30j (${startDateStr} → ${endDateStr})`);
      
      // Charger le résumé avec filtre 30 jours
      const summaryResponse = await apiService.get(
        `/admin/revenue/summary?startDate=${startDateStr}&endDate=${endDateStr}`,
        { userType: 'admin' }
      );
      console.log('📊 Réponse Dashboard:', summaryResponse.summary?.totalRevenue);
      if (summaryResponse.success && summaryResponse.summary) {
        setRevenueSummary(summaryResponse.summary);
      }
    } catch (error) {
      console.error('Erreur chargement revenus (30 jours):', error);
    }
  };

  // Marquer une notification comme lue
  // eslint-disable-next-line no-unused-vars
  const markNotificationAsRead = async (notificationId) => {
    try {
      const response = await apiService.patch(
        `/admin/notifications/${notificationId}/read`,
        {},
        { userType: 'admin' }
      );
      
      if (response.success) {
        // Mettre à jour localement
        const updatedNotifications = notifications.map(notif =>
          notif.id === notificationId ? { ...notif, read: true } : notif
        );
        setNotifications(updatedNotifications);
      }
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  // Marquer toutes les notifications comme lues
  // eslint-disable-next-line no-unused-vars
  const markAllNotificationsAsRead = async () => {
    try {
      const response = await apiService.patch(
        `/admin/notifications/mark-all-read`,
        {},
        { userType: 'admin' }
      );
      
      if (response.success) {
        // Mettre à jour localement
        const updatedNotifications = notifications.map(notif => ({ ...notif, read: true }));
        setNotifications(updatedNotifications);
      }
    } catch (error) {
      console.error('Erreur marquage notifications:', error);
    }
  };

  // Fonction pour approuver un chauffeur via l'API
  const approveDriver = async (driverId, categories = ['standard']) => {
    try {
      console.log('📤 Approbation du chauffeur:', driverId, 'catégories:', categories);
      const response = await apiService.post(
        `/admin/drivers/${driverId}/approve`,
        { categories },
        { userType: 'admin' }
      );
      
      console.log('✅ Réponse serveur:', response);
      
      if (response.success) {
        console.log('✅ Chauffeur approuvé avec succès');
        // Mettre à jour localement
        const updatedDrivers = drivers.map(driver => 
          driver.driver_id === driverId || driver.id === driverId
            ? { ...driver, status: 'approved', categories } 
            : driver
        );
        setDrivers(updatedDrivers);
        setSelectedDriver(null);
        // Recharger les données pour synchroniser
        loadDriversFromAPI();
        // Recharger les notifications pour afficher la nouvelle
        loadNotificationsFromAPI();
      } else {
        console.error('❌ Erreur serveur:', response.error);
        setErrorMessage(response.error || 'Erreur lors de l\'approbation');
      }
    } catch (error) {
      console.error('❌ Erreur approbation chauffeur:', error);
      setErrorMessage(error.error || 'Impossible d\'approuver le chauffeur');
    }
  };

  // Suspendre un chauffeur (depuis DriverDetailsModal)
  const suspendDriver = async (driverId, reason = '') => {
    try {
      const response = await apiService.post(
        `/admin/drivers/${driverId}/suspend`,
        { reason },
        { userType: 'admin' }
      );
      if (response.success) {
        setDrivers(prev => prev.map(d =>
          d.driver_id === driverId ? { ...d, status: 'suspended' } : d
        ));
        setSelectedDriver(null);
        loadDriversFromAPI();
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (error) {
      return { success: false, error: error.error || error.message };
    }
  };

  // Réactiver un chauffeur suspendu (depuis DriverDetailsModal)
  const reactivateDriver = async (driverId) => {
    try {
      const response = await apiService.post(
        `/admin/drivers/${driverId}/approve`,
        {},
        { userType: 'admin' }
      );
      if (response.success) {
        setDrivers(prev => prev.map(d =>
          d.driver_id === driverId ? { ...d, status: 'approved' } : d
        ));
        setSelectedDriver(null);
        loadDriversFromAPI();
        return { success: true };
      }
      return { success: false, error: response.error };
    } catch (error) {
      return { success: false, error: error.error || error.message };
    }
  };

  // Fonction pour rejeter un chauffeur via l'API
  const rejectDriver = async (driverId, reason = '') => {
    try {
      const response = await apiService.post(
        `/admin/drivers/${driverId}/reject`,
        { reason },
        { userType: 'admin' }
      );

      if (response.success) {
        // Mettre à jour localement
        const updatedDrivers = drivers.map(driver => 
          driver.driver_id === driverId || driver.id === driverId
            ? { ...driver, status: 'rejected', rejectionReason: reason } 
            : driver
        );
        setDrivers(updatedDrivers);
        setSelectedDriver(null);
        // Recharger les données pour synchroniser
        loadDriversFromAPI();
      } else {
        setErrorMessage(response.error || 'Erreur lors du rejet');
      }
    } catch (error) {
      console.error('Erreur rejet chauffeur:', error);
      setErrorMessage(error.error || 'Impossible de rejeter le chauffeur');
    }
  };

  // Statistiques
  const stats = {
    totalRevenue: revenueSummary.totalRevenue ? revenueSummary.totalRevenue.toLocaleString() : '0',
    totalSubscribers: subscriptions.length,
    totalDrivers: drivers.length,
    pendingDrivers: drivers.filter(d => d.status === 'pending').length,
    approvedDrivers: drivers.filter(d => d.status === 'approved').length,
    rejectedDrivers: drivers.filter(d => d.status === 'rejected').length,
    totalRoutes: routes.length || 0,
    activeRoutes: routes.filter(r => r.active).length || 0,
  };

  // Pastille WhatsApp : messages reçus non lus (boîte de réception, 2026-10-30). Relevé toutes les 60 s.
  const [waUnread, setWaUnread] = useState(0);
  const canSeeWhatsApp = isAuthenticated && !!currentAdmin && canAccessPage('whatsapp');
  useEffect(() => {
    if (!canSeeWhatsApp) return undefined;
    let alive = true;
    const tick = () => apiService.get('/admin/whatsapp/conversations?limit=1', { userType: 'admin', silent401: true })
      .then((r) => { if (alive) setWaUnread(r.unread || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [canSeeWhatsApp, currentPage]);

  // Badge « bulle + chiffre » par onglet = notifications admin NON LUES de la catégorie.
  const tabBadges = {};
  notifications.forEach((n) => {
    if (n.read) return;
    const page = NOTIF_PAGE_MAP[n.notifType];
    if (page) tabBadges[page] = (tabBadges[page] || 0) + 1;
  });

  // Ouvrir un onglet efface son badge : ses notifications sont marquées lues.
  useEffect(() => {
    const toRead = notifications.filter((n) => !n.read && NOTIF_PAGE_MAP[n.notifType] === currentPage);
    if (toRead.length === 0) return;
    toRead.forEach((n) => {
      apiService.patch(`/admin/notifications/${n.id}/read`, {}, { userType: 'admin' }).catch(() => {});
    });
    setNotifications((prev) => prev.map((n) => (NOTIF_PAGE_MAP[n.notifType] === currentPage ? { ...n, read: true } : n)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, notifications]);

  // Ancien planDetails - remplacé par PLAN_DETAILS (constante) pour éviter les re-renders
  // Ne pas utiliser planDetails ci-dessous

  // payments supprimé — chargé dynamiquement dans PaymentsPage via API

  // ==================== PAGES D'AUTHENTIFICATION ====================



  // ==================== PANELS FONCTIONNELS ====================

  // Formater le message d'une notification en utilisant les données enrichies
  const formatNotifMessage = (notif) => {
    const d = notif.data || {};
    const score = notif.message.match(/(\d+\/5)/)?.[1];
    switch (notif.notifType) {
      case 'bad_rating_client': {
        const name = d.user_name || d.user_id || '?';
        const pid = d.user_public_id ? ` (${d.user_public_id})` : '';
        const ride = d.ride_id ? ` · course #${d.ride_id.slice(-6)}` : '';
        return `⚠️ Mauvaise note (${score || '?/5'}) — ${name}${pid}${ride}`;
      }
      case 'bad_rating_driver': {
        const name = d.driver_name || d.driver_id || '?';
        const pid = d.driver_public_id ? ` (${d.driver_public_id})` : '';
        const ride = d.ride_id ? ` · course #${d.ride_id.slice(-6)}` : '';
        return `⚠️ Mauvaise note (${score || '?/5'}) — ${name}${pid}${ride}`;
      }
      case 'bad_rating_supplier': {
        const name = d.supplier_name || d.supplier_id || '?';
        return `⚠️ Mauvaise note (${score || '?/5'}) — commerce ${name}`;
      }
      case 'driver_rating_watch': {
        const name = d.driver_name || d.driver_id || '?';
        const pid = d.driver_public_id ? ` (${d.driver_public_id})` : '';
        return `👀 À surveiller — ${name}${pid} : ${d.low_count || '?'} notes ≤ 2`;
      }
      default:
        return notif.message;
    }
  };

  // Page de navigation d'une notification : même table que les pastilles,
  // et seulement si l'admin a accès à cette page (sinon pas de navigation).
  const getNotifPage = (notifType) => {
    const page = NOTIF_PAGE_MAP[notifType];
    return page && canAccessPage(page) ? page : null;
  };

  const handleNotifClick = async (notif) => {
    if (!notif.read) await markNotificationAsRead(notif.id);
    const page = getNotifPage(notif.notifType);
    if (page) {
      setCurrentPage(page);
      setShowNotifications(false);
    }
  };

  const NotificationsPanel = () => (
    <div className="dropdown-panel notifications-panel">
      <div className="panel-header">
        <h3>Notifications</h3>
        <span className="badge">{unreadCount}</span>
      </div>
      <div className="notifications-list">
        {notifications.length === 0 && (
          <div style={{ padding: '20px', textAlign: 'center', color: '#9AA0A8', fontSize: 14 }}>
            Aucune notification
          </div>
        )}
        {notifications.map(notif => {
          const page = getNotifPage(notif.notifType);
          return (
            <div
              key={notif.id}
              className={`notification-item ${notif.read ? 'read' : ''}`}
              onClick={() => handleNotifClick(notif)}
              style={{ cursor: page ? 'pointer' : 'default' }}
            >
              <div className="notification-icon">
                {notif.type === 'driver' && <Car size={18} />}
                {notif.type === 'payment' && <DollarSign size={18} />}
                {notif.type === 'client' && <Users size={18} />}
              </div>
              <div className="notification-content">
                <p>{formatNotifMessage(notif)}</p>
                <span className="notification-time">
                  {notif.time}
                  {page && <span style={{ marginLeft: 6, fontSize: 11, color: '#F54119' }}>→ {page}</span>}
                </span>
              </div>
              {!notif.read && <div className="notification-dot"></div>}
            </div>
          );
        })}
      </div>
      <div className="panel-footer">
        <button className="btn-text" onClick={markAllNotificationsAsRead}>Tout marquer comme lu</button>
        <button className="btn-text" onClick={() => { setShowNotifications(false); }}>Fermer</button>
      </div>
    </div>
  );

  const UserMenuPanel = () => (
    <div className="dropdown-panel user-menu-panel">
      <div className="user-info-large">
        <div className="user-avatar-large">
          {currentAdmin?.name?.charAt(0) || 'A'}
        </div>
        <div>
          <h3>{currentAdmin?.name || 'Admin'}</h3>
          <p>{currentAdmin?.email || 'admin@mbiyo.org'}</p>
        </div>
      </div>
      <div className="menu-divider"></div>
      <div className="menu-items">
        <div className="menu-item-dropdown" onClick={() => { setShowUserMenu(false); setCurrentPage('settings'); }}>
          <Settings size={18} />
          <span>Paramètres</span>
        </div>
        <div className="menu-item-dropdown" onClick={() => { setShowUserMenu(false); setShowNotifications(true); }}>
          <Bell size={18} />
          <span>Notifications</span>
          {unreadCount > 0 && <span className="badge-mini">{unreadCount}</span>}
        </div>
      </div>
      <div className="menu-divider"></div>
      <div className="menu-items">
        <div 
          className="menu-item-dropdown logout" 
          onClick={() => {
            authServiceAdmin.logout();
            setIsAuthenticated(false);
            setCurrentAdmin(null);
            setCurrentPage('login');
            setShowUserMenu(false);
          }}
        >
          <LogOut size={18} />
          <span>Se déconnecter</span>
        </div>
      </div>
    </div>
  );


  // ==================== MODALS ====================

  // ==================== PAGE PARAMÈTRES PLATEFORME ====================


  // ==================== PAGE SOLDES & VIREMENTS ====================


  const DriverDetailsModal = ({ driver: initialDriver, onClose, onApprove, onReject, onSuspend, onReactivate, onOpenDriver }) => {
    const [driver, setDriver] = React.useState(initialDriver);
    // Parrain (referred_by = ID public du parrain) : chargé pour afficher la carte « Parrainé par »
    const [sponsor, setSponsor] = useState(null);
    const [sponsorError, setSponsorError] = useState(false);
    useEffect(() => {
      setSponsor(null);
      if (!driver?.referred_by) return;
      setSponsorError(false);
      apiService.get(`/admin/drivers/${encodeURIComponent(driver.referred_by)}/details`, { userType: 'admin' })
        .then(r => { if (r?.success && r.driver) setSponsor(r.driver); else setSponsorError(true); })
        .catch(() => setSponsorError(true));
    }, [driver?.referred_by]);
    const [, setCityTick] = useState(0);
    useEffect(() => { if (!Object.keys(CITY_NAMES).length) loadCityNames().then(() => setCityTick(x => x + 1)); }, []);
    // Selfies de sécurité : historique personnel du chauffeur (30 derniers jours, purge automatique au-delà)
    const [selfieHistory, setSelfieHistory] = useState(null);
    useEffect(() => {
      setSelfieHistory(null);
      if (!driver?.driver_id) return;
      apiService.get(`/safety/admin/drivers/${encodeURIComponent(driver.driver_id)}/selfies`, { userType: 'admin' })
        .then(r => setSelfieHistory(r?.success ? r : { checks: [] }))
        .catch(() => setSelfieHistory({ checks: [] }));
    }, [driver?.driver_id]);
    const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [driverScanStats, setDriverScanStats] = useState({ today: 0, week: 0, month: 0, year: 0, total: 0, filterPeriod: 'today' });
    const [loadingScanStats, setLoadingScanStats] = useState(true);
    const [lightboxIndex, setLightboxIndex] = useState(-1); // -1 = fermé
    // Re-validation d'un chauffeur déjà approuvé : ses catégories actuelles sont préremplies (sinon standard)
    const [approveCategories, setApproveCategories] = useState(initialDriver?.categories?.length ? initialDriver.categories : ['standard']);

    // Livreur sans véhicule : on masque les sections véhicule/photos/catégories (courses).
    const hasVehicle = !!(driver.vehicle?.plate || driver.vehicle?.brand || driver.vehicle?.model);

    // État pour le Compte SOLDE
    const [balanceData, setBalanceData] = useState(null);
    const [balanceRides, setBalanceRides] = useState(null);
    const [balancePeriod, setBalancePeriod] = useState('week');
    const [loadingBalance, setLoadingBalance] = useState(false);
    const [adjustAmount, setAdjustAmount] = useState('');
    const [adjustDirection, setAdjustDirection] = useState('credit');
    const [adjustNote, setAdjustNote] = useState('');
    const [showAdjustForm, setShowAdjustForm] = useState(false);
    const [showBalanceTab, setShowBalanceTab] = useState(false);
    const [showRatingsTab, setShowRatingsTab] = useState(false);

    // Fournisseur affilié (si chauffeur avec supplier_code)
    const [supplierInfo, setSupplierInfo] = useState(null);
    const [loadingSupplier, setLoadingSupplier] = useState(false);

    const toggleApproveCategory = (cat) => {
      setApproveCategories(prev => {
        if (prev.includes(cat)) {
          // Ne pas permettre de tout décocher
          if (prev.length === 1) return prev;
          return prev.filter(c => c !== cat);
        }
        return fillCategoryGaps([...prev, cat]);
      });
    };

    // Récupérer les stats de scans du chauffeur
    React.useEffect(() => {
      if (driver?.driver_id) {
        const fetchScanStats = async () => {
          try {
            const data = await apiService.get(`/drivers/${driver.driver_id}/stats`, { userType: 'admin' });
            if (data.success && data.stats) {
              setDriverScanStats(data.stats);
            }
          } catch (error) {
            console.error('Erreur récupération stats:', error);
          } finally {
            setLoadingScanStats(false);
          }
        };
        fetchScanStats();
      }
    }, [driver?.driver_id]);

    // Récupérer les données de solde
    const fetchBalance = async () => {
      if (!driver?.driver_id) return;
      setLoadingBalance(true);
      try {
        const [balRes, ridesRes] = await Promise.all([
          apiService.get(`/balance/driver/${driver.driver_id}`, { userType: 'admin' }),
          apiService.get(`/balance/driver/${driver.driver_id}/rides?period=${balancePeriod}`, { userType: 'admin' })
        ]);
        if (balRes.success) setBalanceData(balRes.balance);
        if (ridesRes.success) setBalanceRides(ridesRes);
      } catch (error) {
        console.error('Erreur récupération solde:', error);
      } finally {
        setLoadingBalance(false);
      }
    };

    React.useEffect(() => {
      if (showBalanceTab && driver?.driver_id) {
        fetchBalance();
      }
    }, [showBalanceTab, balancePeriod, driver?.driver_id]);

    // Charger les infos du fournisseur si chauffeur affilié
    React.useEffect(() => {
      if (driver?.supplier_id) {
        setLoadingSupplier(true);
        apiService.get(`/delivery/suppliers/${driver.supplier_id}`, { userType: 'admin' })
          .then(res => {
            const sup = res.supplier || res.data?.supplier;
            if (sup) setSupplierInfo(sup);
          })
          .catch(() => {})
          .finally(() => setLoadingSupplier(false));
      }
    }, [driver?.supplier_id]);


    const handleUnblock = async () => {
      try {
        await apiService.post(`/balance/driver/${driver.driver_id}/unblock`, {}, { userType: 'admin' });
        await fetchBalance();
      } catch (error) {
        alert('Erreur déblocage: ' + error.message);
      }
    };

    const handleAdjust = async () => {
      if (!adjustAmount || isNaN(adjustAmount) || Number(adjustAmount) <= 0) return alert('Montant invalide');
      try {
        await apiService.post(`/balance/driver/${driver.driver_id}/adjust`, {
          amount: Number(adjustAmount),
          direction: adjustDirection,
          note: adjustNote || undefined
        }, { userType: 'admin' });
        setShowAdjustForm(false);
        setAdjustAmount('');
        setAdjustNote('');
        await fetchBalance();
      } catch (error) {
        alert('Erreur ajustement: ' + error.message);
      }
    };

    const handleUploadPhoto = async (photoType) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingPhoto(photoType);
        try {
          const formData = new FormData();
          formData.append('photo', file);
          formData.append('photo_type', photoType);
          const res = await apiService.upload(`/drivers/${driver.driver_id}/photo`, formData, { userType: 'admin' });
          if (res.success) {
            setDriver(prev => ({
              ...prev,
              photo: res.driver?.photo ?? prev.photo,
              id_document: res.driver?.id_document ?? prev.id_document,
              license_document: res.driver?.license_document ?? prev.license_document,
            }));
            alert(`Photo (${photoType}) uploadée avec succès !`);
          }
        } catch (err) {
          alert('Erreur upload: ' + err.message);
        } finally {
          setUploadingPhoto(false);
        }
      };
      input.click();
    };

    const handleManualPayout = async () => {
      if (!window.confirm('Effectuer le virement maintenant ?')) return;
      try {
        const res = await apiService.post('/balance/payout/manual', { driver_id: driver.driver_id }, { userType: 'admin' });
        alert(res.message);
        await fetchBalance();
      } catch (error) {
        alert('Erreur virement: ' + error.message);
      }
    };

    if (!driver) return null;

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Validation du chauffeur</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            {driver.status === 'pending' && ['document_update', 'vehicle_photo', 'vehicle_update'].includes(driver.pending_kind) && (
              <div style={{ background: '#FFF7ED', border: '1px solid #FDBA74', borderRadius: 12, padding: '10px 14px', marginBottom: 14, color: '#9A3412', fontSize: 13 }}>
                <strong>Re-validation d'un chauffeur déjà approuvé</strong> — {{
                  document_update: 'il a ajouté ou remplacé une pièce d\'identité ou un permis de conduire',
                  vehicle_photo: 'il a ajouté une photo de véhicule',
                  vehicle_update: 'il a modifié les détails de son véhicule',
                }[driver.pending_kind]}. Vérifiez les documents ci-dessous puis approuvez (ses catégories sont préremplies) ou rejetez.
              </div>
            )}
            <div className="info-section">
              <h3>Informations personnelles</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Nom complet</label>
                  <p>{driver.name}</p>
                </div>
                <div className="info-item">
                  <label>Téléphone</label>
                  <p>{driver.phone}</p>
                </div>
                <div className="info-item">
                  <label>Email</label>
                  <p>{driver.email || 'Non fourni'}</p>
                </div>
                <div className="info-item">
                  <label>Date d'inscription</label>
                  <p>{driver.created_at ? new Date(driver.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Ville de travail</label>
                  <p>📍 {cityName(driver.city_slug)}{driver.city_slug ? <span style={{ color: '#9AA0A8', fontSize: 12, marginLeft: 6 }}>({driver.city_slug})</span> : null}</p>
                </div>
              </div>
            </div>

            {/* Parrainage : affiché seulement si le chauffeur a été parrainé */}
            {driver.referred_by && (
              <div className="info-section" style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: 12, padding: '12px 16px' }}>
                <h3 style={{ color: '#5B21B6' }}>🤝 Parrainé par</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    title="Voir le détail du parrain"
                    onClick={() => sponsor && onOpenDriver && onOpenDriver(sponsor)}
                    disabled={!sponsor}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #C4B5FD', borderRadius: 10, padding: '8px 12px', cursor: sponsor ? 'pointer' : 'default', textAlign: 'left' }}
                  >
                    {sponsor?.photo ? <Img src={sponsor.photo} alt={sponsor.name} style={{ width: 36, height: 36, borderRadius: 18, objectFit: 'cover' }} />
                      : <div style={{ width: 36, height: 36, borderRadius: 18, background: '#DDD6FE', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5B21B6', fontWeight: 700 }}>{(sponsor?.name || '?').charAt(0)}</div>}
                    <div>
                      <div style={{ fontWeight: 700, color: '#1E2229' }}>{sponsor ? sponsor.name : (sponsorError ? 'Parrain introuvable' : 'Chargement…')}</div>
                      <div style={{ fontSize: 12, color: '#7C3AED', fontFamily: 'monospace', textDecoration: 'underline' }}>{driver.referred_by}</div>
                    </div>
                  </button>
                  <div style={{ fontSize: 13, color: '#5C636E' }}>
                    {sponsor?.phone && <div>📞 {sponsor.phone}</div>}
                    {sponsor?.status && <div>Statut du parrain : <strong>{sponsor.status}</strong></div>}
                    <div>
                      {driver.referral_paid_at
                        ? <>Bonus versés le {new Date(driver.referral_paid_at).toLocaleDateString('fr-FR')}</>
                        : <>Bonus en attente — versés à la <strong>première mission terminée</strong> du filleul</>}
                    </div>
                  </div>
                </div>
                {sponsorError && <p style={{ fontSize: 12, color: '#9AA0A8', margin: '8px 0 0' }}>Aucun chauffeur avec cet ID public dans votre périmètre (ville), ou compte supprimé.</p>}
              </div>
            )}

            <div className="info-section">
              <h3>Documents</h3>
              <div className="photos-grid">
                <div className="photo-item">
                  <label>Photo de profil</label>
                  {driver.photo ? (
                    <Img src={driver.photo} alt="Profil" className="doc-image profile" />
                  ) : (
                    <div className="no-photo">Aucune photo</div>
                  )}
                  <button
                    onClick={() => handleUploadPhoto('profile')}
                    disabled={uploadingPhoto === 'profile'}
                    style={{ marginTop: 8, padding: '6px 14px', background: '#FF7800', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    {uploadingPhoto === 'profile' ? 'Upload...' : '📤 Uploader photo'}
                  </button>
                </div>
                <div className="photo-item">
                  <label>Carte d'identité</label>
                  {driver.id_document ? (
                    <DocumentCompare current={driver.id_document} previous={driver.id_document_previous} replacedAt={driver.id_document_previous_at} alt="Carte d'identité" />
                  ) : (
                    <div className="no-photo">Aucune carte d'identité</div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {driver.id_number
                      ? <span><strong>N° :</strong> {driver.id_number}</span>
                      : <span style={{ color: '#9AA0A8' }}>Numéro non fourni</span>}
                  </div>
                  <button
                    onClick={() => handleUploadPhoto('id_document')}
                    disabled={uploadingPhoto === 'id_document'}
                    style={{ marginTop: 8, padding: '6px 14px', background: '#FF7800', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    {uploadingPhoto === 'id_document' ? 'Upload...' : '📤 Uploader la carte d\'identité'}
                  </button>
                </div>
                <div className="photo-item">
                  <label>Permis de conduire</label>
                  {driver.license_document ? (
                    <DocumentCompare current={driver.license_document} previous={driver.license_document_previous} replacedAt={driver.license_document_previous_at} alt="Permis de conduire" />
                  ) : (
                    <div className="no-photo">Aucun permis</div>
                  )}
                  <div style={{ marginTop: 6, fontSize: 13 }}>
                    {driver.license_number
                      ? <span><strong>N° :</strong> {driver.license_number}</span>
                      : <span style={{ color: '#9AA0A8' }}>Numéro non fourni</span>}
                  </div>
                  <button
                    onClick={() => handleUploadPhoto('license')}
                    disabled={uploadingPhoto === 'license'}
                    style={{ marginTop: 8, padding: '6px 14px', background: '#FF7800', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
                  >
                    {uploadingPhoto === 'license' ? 'Upload...' : '📤 Uploader le permis'}
                  </button>
                </div>
              </div>
            </div>

            <div className="info-section">
              <h3>Selfies de sécurité — 30 derniers jours</h3>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#9AA0A8' }}>
                Vérification périodique anti prêt de compte (réglages dans le Centre de sécurité). Historique personnel conservé {selfieHistory?.retention_days || 30} jours, puis supprimé automatiquement.
                {selfieHistory?.next_selfie_due_at && <> Prochain selfie exigé le {new Date(selfieHistory.next_selfie_due_at).toLocaleDateString('fr-FR', { dateStyle: 'medium' })}.</>}
              </p>
              {selfieHistory === null ? (
                <div style={{ color: '#9AA0A8', fontSize: 13 }}>Chargement…</div>
              ) : selfieHistory.checks.length === 0 ? (
                <div style={{ color: '#9AA0A8', fontSize: 13 }}>Aucun selfie sur les 30 derniers jours.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
                  {selfieHistory.checks.map(c => {
                    const verdict = { approved: ['✓ Validé', '#ECFDF5', '#047857'], rejected: ['✗ Refusé', '#FEE2E2', '#B91C1C'], submitted: ['⏳ À vérifier', '#FFF7ED', '#9A3412'], required: ['Demandé', '#F4F5F7', '#5C636E'], suspended: ['Levé (pause)', '#F4F5F7', '#9AA0A8'] }[c.status] || [c.status, '#F4F5F7', '#5C636E'];
                    const when = c.submitted_at || c.required_at;
                    return (
                      <div key={c.check_id} style={{ border: '1px solid #E9EBEF', borderRadius: 12, padding: 8, background: '#fff' }}>
                        {c.photo_url ? (
                          <Img src={c.photo_url} alt="Selfie" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' }} onClick={() => window.open(c.photo_url, '_blank', 'noopener')} />
                        ) : (
                          <div style={{ width: '100%', aspectRatio: '1', borderRadius: 8, background: '#F4F5F7', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9AA0A8', fontSize: 12 }}>Non envoyé</div>
                        )}
                        <div style={{ marginTop: 6, display: 'inline-block', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: verdict[1], color: verdict[2] }}>{verdict[0]}</div>
                        <div style={{ fontSize: 11, color: '#5C636E', marginTop: 4 }}>{when ? new Date(when).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</div>
                        {c.reviewed_by && <div style={{ fontSize: 11, color: '#9AA0A8' }}>par {c.reviewed_by}</div>}
                        {c.review_note && <div style={{ fontSize: 11, color: '#B91C1C', marginTop: 2 }}>{c.review_note}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {hasVehicle && (
            <div className="info-section">
              <h3>Informations du véhicule</h3>
              <div className="vehicle-card">
                <div className="vehicle-icon">
                  <Car size={32} />
                </div>
                <div className="vehicle-details">
                  <div className="vehicle-row">
                    <span className="vehicle-label">Marque:</span>
                    <span className="vehicle-value">{driver.vehicle?.brand}</span>
                  </div>
                  <div className="vehicle-row">
                    <span className="vehicle-label">Modèle:</span>
                    <span className="vehicle-value">{driver.vehicle?.model}</span>
                  </div>
                  <div className="vehicle-row">
                    <span className="vehicle-label">Couleur:</span>
                    <span className="vehicle-value">
                      {vehicleColorLabel(driver.vehicle?.color)}
                      {(!driver.vehicle?.color || driver.vehicle?.color === 'other') && (
                        <span title={driver.vehicle?.color_raw ? `Saisie d'origine : ${driver.vehicle.color_raw}` : ''} style={{ marginLeft: 6, background: '#FEF3C7', color: '#B45309', borderRadius: 8, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>à préciser{driver.vehicle?.color_raw ? ` (${driver.vehicle.color_raw})` : ''}</span>
                      )}
                    </span>
                  </div>
                  <div className="vehicle-row">
                    <span className="vehicle-label">Plaque:</span>
                    <span className="vehicle-value plate">{driver.vehicle?.plate}</span>
                  </div>
                  {driver.vehicle?.year && (
                    <div className="vehicle-row">
                      <span className="vehicle-label">Année:</span>
                      <span className="vehicle-value">{driver.vehicle.year}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            )}

            {/* Photos du véhicule */}
            {hasVehicle && driver.vehicle?.photos && driver.vehicle.photos.length > 0 && (
              <div className="info-section">
                <h3>📷 Photos du véhicule ({driver.vehicle.photos.length})</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                  {driver.vehicle.photos.map((photo, idx) => (
                    <div key={idx} style={{ borderRadius: '12px', overflow: 'hidden', border: '2px solid #E9EBEF', aspectRatio: '4/3', cursor: 'pointer', position: 'relative' }}
                      onClick={() => setLightboxIndex(idx)}>
                      <Img src={photo} alt={`Véhicule ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      <div style={{ position: 'absolute', bottom: '6px', right: '6px', background: 'rgba(0,0,0,0.6)', color: 'white', borderRadius: '6px', padding: '2px 8px', fontSize: '0.75rem', fontWeight: '600' }}>{idx + 1}/{driver.vehicle.photos.length}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {hasVehicle && (!driver.vehicle?.photos || driver.vehicle.photos.length === 0) && (
              <div className="info-section">
                <h3>📷 Photos du véhicule</h3>
                <p style={{ color: '#9AA0A8', fontStyle: 'italic', fontSize: '0.9rem' }}>Aucune photo de véhicule importée</p>
              </div>
            )}

            {/* Lightbox photos véhicule */}
            {lightboxIndex >= 0 && driver.vehicle?.photos && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.92)', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }} onClick={() => setLightboxIndex(-1)}>
                {/* Header */}
                <div style={{ position: 'absolute', top: '16px', left: 0, right: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 24px', zIndex: 10001 }}>
                  <span style={{ color: 'white', fontSize: '1rem', fontWeight: '600', background: 'rgba(255,255,255,0.15)', padding: '6px 14px', borderRadius: '20px' }}>
                    {lightboxIndex + 1} / {driver.vehicle.photos.length}
                  </span>
                  <button onClick={() => setLightboxIndex(-1)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', width: '40px', height: '40px', borderRadius: '50%', fontSize: '1.4rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                </div>
                {/* Photo */}
                <Img src={driver.vehicle.photos[lightboxIndex]} alt={`Véhicule ${lightboxIndex + 1}`} onClick={e => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} />
                {/* Navigation arrows */}
                {driver.vehicle.photos.length > 1 && (
                  <>
                    <button onClick={e => { e.stopPropagation(); setLightboxIndex(lightboxIndex <= 0 ? driver.vehicle.photos.length - 1 : lightboxIndex - 1); }} style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '48px', height: '48px', borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>‹</button>
                    <button onClick={e => { e.stopPropagation(); setLightboxIndex(lightboxIndex >= driver.vehicle.photos.length - 1 ? 0 : lightboxIndex + 1); }} style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '48px', height: '48px', borderRadius: '50%', fontSize: '1.5rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>›</button>
                  </>
                )}
                {/* Thumbnails */}
                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }} onClick={e => e.stopPropagation()}>
                  {driver.vehicle.photos.map((photo, idx) => (
                    <div key={idx} onClick={() => setLightboxIndex(idx)} style={{ width: '56px', height: '42px', borderRadius: '6px', overflow: 'hidden', cursor: 'pointer', border: idx === lightboxIndex ? '2px solid white' : '2px solid transparent', opacity: idx === lightboxIndex ? 1 : 0.5, transition: 'all 0.2s' }}>
                      <Img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section fournisseur — visible seulement si chauffeur affilié */}
            {(driver.supplier_code || driver.supplier_id) && (
              <div className="info-section" style={{ borderLeft: '4px solid #FFB900', paddingLeft: '12px' }}>
                <h3 style={{ color: '#C9350F' }}>🏢 Chauffeur affilié à un fournisseur</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Code fournisseur</label>
                    <p style={{ fontFamily: 'monospace', fontWeight: 700, color: '#1E3A5F', fontSize: '1rem' }}>
                      {driver.supplier_code || '—'}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>ID fournisseur</label>
                    <p style={{ fontSize: '0.8rem', color: '#5C636E', wordBreak: 'break-all' }}>
                      {driver.supplier_id || '—'}
                    </p>
                  </div>
                </div>

                {loadingSupplier ? (
                  <p style={{ color: '#9AA0A8', fontStyle: 'italic', fontSize: '0.875rem' }}>Chargement des infos fournisseur…</p>
                ) : supplierInfo ? (
                  <div style={{ marginTop: '12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#F59E0B22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🏪</div>
                      <div>
                        <p style={{ fontWeight: '700', color: '#1C1C1C', margin: 0, fontSize: '1rem' }}>{supplierInfo.business_name}</p>
                        <p style={{ fontSize: '0.8rem', color: '#5C636E', margin: 0 }}>
                          {supplierInfo.business_type === 'vehicle_rental' ? '🚗 Location véhicule' : supplierInfo.business_type}
                        </p>
                      </div>
                      <span style={{
                        marginLeft: 'auto', padding: '3px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: '700',
                        background: supplierInfo.status === 'approved' ? '#D1FAE5' : '#FEE2E2',
                        color: supplierInfo.status === 'approved' ? '#065F46' : '#991B1B',
                      }}>
                        {supplierInfo.status === 'approved' ? '✅ Validé' : supplierInfo.status === 'pending' ? '⏳ En attente' : '❌ Refusé'}
                      </span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.875rem' }}>
                      {supplierInfo.phone && (
                        <div><span style={{ color: '#9AA0A8' }}>📞 </span><span style={{ color: '#414852' }}>{supplierInfo.phone}</span></div>
                      )}
                      {supplierInfo.address && (
                        <div><span style={{ color: '#9AA0A8' }}>📍 </span><span style={{ color: '#414852' }}>{supplierInfo.address}</span></div>
                      )}
                      {supplierInfo.rating && (
                        <div><span style={{ color: '#9AA0A8' }}>⭐ </span><span style={{ color: '#414852' }}>{Number(supplierInfo.rating).toFixed(1)} / 5</span></div>
                      )}
                      {supplierInfo.city_slug && (
                        <div><span style={{ color: '#9AA0A8' }}>🏙️ </span><span style={{ color: '#414852', textTransform: 'capitalize' }}>{supplierInfo.city_slug}</span></div>
                      )}
                    </div>
                  </div>
                ) : driver.supplier_id ? (
                  <p style={{ color: '#DC2626', fontSize: '0.875rem', marginTop: '8px' }}>⚠️ Fournisseur introuvable (ID: {driver.supplier_id})</p>
                ) : null}
              </div>
            )}

            <div className="info-section">
              <h3>Statut</h3>
              <div className={`status-badge status-${driver.status}`}>
                {driver.status === 'pending' && '⏳ En attente'}
                {driver.status === 'approved' && '✅ Approuvé'}
                {driver.status === 'rejected' && '❌ Rejeté'}
              </div>
            </div>

            {driver.status === 'approved' && !loadingScanStats && (
              <>
                <div className="info-section">
                  <h3>📊 Scans Valides (Filtre par période)</h3>
                  <div style={{marginBottom: '12px'}}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
                      <button
                        onClick={() => setDriverScanStats({...driverScanStats, filterPeriod: 'today'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (driverScanStats.filterPeriod || 'today') === 'today' ? '#C9350F' : '#E9EBEF',
                          color: (driverScanStats.filterPeriod || 'today') === 'today' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Aujourd'hui
                      </button>
                      <button
                        onClick={() => setDriverScanStats({...driverScanStats, filterPeriod: 'week'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (driverScanStats.filterPeriod || 'today') === 'week' ? '#C9350F' : '#E9EBEF',
                          color: (driverScanStats.filterPeriod || 'today') === 'week' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Semaine
                      </button>
                      <button
                        onClick={() => setDriverScanStats({...driverScanStats, filterPeriod: 'month'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (driverScanStats.filterPeriod || 'today') === 'month' ? '#C9350F' : '#E9EBEF',
                          color: (driverScanStats.filterPeriod || 'today') === 'month' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Mois
                      </button>
                      <button
                        onClick={() => setDriverScanStats({...driverScanStats, filterPeriod: 'year'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (driverScanStats.filterPeriod || 'today') === 'year' ? '#C9350F' : '#E9EBEF',
                          color: (driverScanStats.filterPeriod || 'today') === 'year' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Année
                      </button>
                      <button
                        onClick={() => setDriverScanStats({...driverScanStats, filterPeriod: 'total'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (driverScanStats.filterPeriod || 'today') === 'total' ? '#C9350F' : '#E9EBEF',
                          color: (driverScanStats.filterPeriod || 'today') === 'total' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Total
                      </button>
                    </div>
                  </div>
                  <div style={{padding: '2rem', background: 'linear-gradient(135deg, #FEE9E2 0%, #FEE9E2 100%)', borderRadius: '12px', textAlign: 'center', border: '2px solid #C9350F'}}>
                    <p style={{fontSize: '0.95rem', color: '#C9350F', marginBottom: '0.5rem', fontWeight: '500'}}>
                      {(driverScanStats.filterPeriod || 'today') === 'today' && 'Scans valides aujourd\'hui'}
                      {(driverScanStats.filterPeriod || 'today') === 'week' && 'Scans valides cette semaine'}
                      {(driverScanStats.filterPeriod || 'today') === 'month' && 'Scans valides ce mois'}
                      {(driverScanStats.filterPeriod || 'today') === 'year' && 'Scans valides cette année'}
                      {(driverScanStats.filterPeriod || 'today') === 'total' && 'Scans valides (total depuis le début)'}
                    </p>
                    <p style={{fontSize: '2.5rem', fontWeight: '700', color: '#C9350F', margin: '0.5rem 0'}}>
                      {(driverScanStats.filterPeriod || 'today') === 'today' && driverScanStats.today}
                      {(driverScanStats.filterPeriod || 'today') === 'week' && driverScanStats.week}
                      {(driverScanStats.filterPeriod || 'today') === 'month' && driverScanStats.month}
                      {(driverScanStats.filterPeriod || 'today') === 'year' && driverScanStats.year}
                      {(driverScanStats.filterPeriod || 'today') === 'total' && driverScanStats.total}
                    </p>
                  </div>
                </div>

                <div className="info-section">
                  <h3>📈 Performance & Engagement</h3>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                    <div style={{padding: '1rem', background: '#FFFFFF', borderRadius: '8px', borderLeft: '4px solid #10B981'}}>
                      <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>Moyenne/jour (mois)</p>
                      <p style={{fontSize: '1.3rem', fontWeight: '600', color: '#10B981'}}>
                        {driverScanStats.month > 0 ? (driverScanStats.month / 30).toFixed(1) : 0}
                      </p>
                    </div>
                    <div style={{padding: '1rem', background: '#FFFFFF', borderRadius: '8px', borderLeft: '4px solid #FFB900'}}>
                      <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>Note du chauffeur</p>
                      <p style={{fontSize: '1.3rem', fontWeight: '600', color: '#FFB900'}}>
                        ⭐ {driver.rating || 5.0}/5.0 ({driver.rating_count || 0} avis)
                      </p>
                      <div style={{marginTop: '0.8rem', fontSize: '0.85rem', color: '#555'}}>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem'}}>
                          <div>Professionnalisme: {driver.professionalism_score?.toFixed(1) || '5.0'}/5</div>
                          <div>Propreté: {driver.cleanliness_score?.toFixed(1) || '5.0'}/5</div>
                          <div>Communication: {driver.communication_score?.toFixed(1) || '5.0'}/5</div>
                          <div>Conduite: {driver.driving_score?.toFixed(1) || '5.0'}/5</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Offres de dispatch non honorées (tracé, sans pénalité) */}
            {((driver.delivery_offer_declines || 0) > 0 || (driver.delivery_offer_expiries || 0) > 0) && (
              <div className="info-section">
                <h3>🎯 Offres de livraison (dispatch)</h3>
                <p style={{ fontSize: '0.85rem', color: '#666', margin: 0 }}>
                  Refusées : <strong>{driver.delivery_offer_declines || 0}</strong> · Expirées sans réponse :{' '}
                  <strong>{driver.delivery_offer_expiries || 0}</strong> <span style={{ color: '#9AA0A8' }}>(tracé informatif — aucune pénalité)</span>
                </p>
              </div>
            )}

            {/* Libérations de livraisons (anti-abus) — visible seulement s'il y en a */}
            {(driver.delivery_releases?.length > 0 || driver.delivery_release_stats?.weighted_count_30d > 0) && (
              <div className="info-section">
                <h3>🔓 Libérations de livraisons</h3>
                <p style={{ fontSize: '0.85rem', color: '#666', margin: '0 0 0.75rem' }}>
                  Commandes rendues avant le retrait (bouton « Je ne peux plus livrer » ou retrait
                  automatique après 20 min sans présentation). Taux pondéré 30 j :{' '}
                  <strong>{((driver.delivery_release_stats?.rate_30d || 0) * 100).toFixed(1)} %</strong>
                  {' '}({driver.delivery_release_stats?.weighted_count_30d || 0} pondéré / {driver.delivery_release_stats?.deliveries_taken_30d || 0} prises)
                  {driver.delivery_release_stats?.penalty > 0 && (
                    <span style={{ color: '#B91C1C' }}> — pénalité de note : -{driver.delivery_release_stats.penalty}</span>
                  )}
                </p>
                {(driver.delivery_releases || []).map((rel) => (
                  <div key={rel.release_id} style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline', fontSize: '0.85rem', padding: '0.35rem 0', borderBottom: '1px solid #F4F5F7' }}>
                    <span style={{ color: '#9AA0A8', whiteSpace: 'nowrap' }}>{new Date(rel.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    <span style={{ fontWeight: 600, color: rel.auto ? '#B91C1C' : '#414852' }}>
                      {rel.auto ? '⏱️ Auto (non présenté)' : { breakdown: '🔧 Panne', too_far: '🗺️ Trop éloigné', other: 'Autre' }[rel.reason] || rel.reason}
                    </span>
                    <span style={{ color: '#5C636E' }}>#{String(rel.order_id).slice(-6)}</span>
                    {rel.note ? <span style={{ color: '#5C636E', fontStyle: 'italic' }}>« {rel.note} »</span> : null}
                  </div>
                ))}
              </div>
            )}

            {/* Catégories du chauffeur */}
            {driver.status === 'approved' && hasVehicle && (
              <div className="info-section">
                <h3>🏷️ Catégories</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(driver.categories || ['standard']).map(cat => (
                    <span key={cat} style={{
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      background: cat === 'premium' ? '#fef3c7' : cat === 'confort' ? '#FEE9E2' : cat === 'moto' ? '#fff7ed' : '#d1fae5',
                      color: cat === 'premium' ? '#C9350F' : cat === 'confort' ? '#C9350F' : cat === 'moto' ? '#c2410c' : '#059669',
                      border: `1px solid ${cat === 'premium' ? '#FFB900' : cat === 'confort' ? '#FF7800' : cat === 'moto' ? '#f97316' : '#10b981'}`
                    }}>
                      {cat === 'premium' ? '⭐ Premium' : cat === 'confort' ? '💺 Confort' : cat === 'moto' ? '🏍️ Moto' : '🚖 Standard'}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {driver.status === 'pending' && !showRejectForm && (
              <>
                {/* Sélection des catégories avant approbation (chauffeurs avec véhicule uniquement) */}
                {hasVehicle && (
                <div className="info-section">
                  <h3>🏷️ Catégories à attribuer</h3>
                  <p style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '10px' }}>Sélectionnez les catégories de courses que ce chauffeur pourra recevoir (plusieurs choix possibles)</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                      { key: 'standard', label: '🚖 Standard', desc: 'Courses Covoiturage & Mbiyo', bg: '#d1fae5', color: '#059669', border: '#10b981' },
                      { key: 'confort', label: '💺 Confort', desc: 'Courses Confort (+30%)', bg: '#FEE9E2', color: '#C9350F', border: '#FF7800' },
                      { key: 'premium', label: '⭐ Premium', desc: 'Courses Premium (+50%)', bg: '#fef3c7', color: '#C9350F', border: '#FFB900' },
                      { key: 'moto', label: '🏍️ Moto', desc: 'Courses Moto (économique)', bg: '#fff7ed', color: '#c2410c', border: '#f97316' }
                    ].map(cat => (
                      <button
                        key={cat.key}
                        onClick={() => toggleApproveCategory(cat.key)}
                        style={{
                          padding: '10px 16px',
                          borderRadius: '12px',
                          border: `2px solid ${approveCategories.includes(cat.key) ? cat.border : '#E9EBEF'}`,
                          background: approveCategories.includes(cat.key) ? cat.bg : '#FFFFFF',
                          color: approveCategories.includes(cat.key) ? cat.color : '#9AA0A8',
                          cursor: 'pointer',
                          fontWeight: '600',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                          flex: '1',
                          minWidth: '100px'
                        }}
                      >
                        <span>{cat.label}</span>
                        <span style={{ fontSize: '0.7rem', fontWeight: '400', opacity: 0.8 }}>{cat.desc}</span>
                      </button>
                    ))}
                  </div>
                  {approveCategories.includes('premium') && approveCategories.includes('standard') && (
                    <p style={{ fontSize: '0.78rem', color: '#B98400', marginTop: '8px', marginBottom: 0 }}>ℹ️ Premium + Standard ⇒ Confort est coché automatiquement : un chauffeur ne peut pas être au-dessus et en dessous d'un niveau sans l'avoir. (Premium seul reste Premium uniquement.)</p>
                  )}
                </div>
                )}

                <div className="modal-actions">
                  <button className="btn-reject" onClick={() => setShowRejectForm(true)}>
                    <XCircle size={20} />
                    Rejeter
                  </button>
                  <button className="btn-approve" onClick={() => onApprove(driver.driver_id, approveCategories)}>
                    <CheckCircle size={20} />
                    Approuver
                  </button>
                </div>
              </>
            )}

            {driver.status === 'approved' && (
              <div style={{ padding: '1rem 0', borderTop: '1px solid #E9EBEF', marginTop: '1rem' }}>
                {/* Compte SOLDE (bouton + onglet) — réservé à Finance / Super Admin uniquement */}
                {canFinance && (
                  <>
                <button
                  onClick={() => setShowBalanceTab(!showBalanceTab)}
                  style={{
                    padding: '12px 20px', backgroundColor: showBalanceTab ? '#059669' : '#C9350F', color: 'white',
                    border: 'none', borderRadius: '8px', fontWeight: '600',
                    cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s', width: '100%', marginBottom: '8px'
                  }}
                >
                  💰 {showBalanceTab ? 'Masquer' : 'Voir'} Compte SOLDE
                </button>

                {/* Onglet Compte SOLDE */}
                {showBalanceTab && (
                  <div style={{ marginTop: '12px' }}>
                    {loadingBalance ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#5C636E' }}>Chargement...</div>
                    ) : balanceData ? (
                      <>
                        {/* Solde actuel */}
                        <div style={{
                          padding: '1.5rem', borderRadius: '12px', textAlign: 'center', marginBottom: '16px',
                          background: balanceData.current_balance >= 0
                            ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                            : 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                          border: `2px solid ${balanceData.current_balance >= 0 ? '#10b981' : '#DC2626'}`
                        }}>
                          <p style={{ fontSize: '0.85rem', color: '#5C636E', marginBottom: '4px' }}>Solde actuel</p>
                          <p style={{
                            fontSize: '2.2rem', fontWeight: '700', margin: '4px 0',
                            color: balanceData.current_balance >= 0 ? '#059669' : '#dc2626'
                          }}>
                            {balanceData.current_balance >= 0 ? '+' : ''}{balanceData.current_balance?.toLocaleString('fr-FR')} CDF
                          </p>
                          {balanceData.account_blocked && (
                            <div style={{
                              background: '#dc2626', color: 'white', padding: '8px 16px',
                              borderRadius: '8px', marginTop: '8px', fontSize: '0.85rem', fontWeight: '600'
                            }}>
                              🚫 COMPTE BLOQUÉ — {balanceData.blocked_reason === 'auto_7days' ? 'Dette > 7 jours' : 'Bloqué par admin'}
                            </div>
                          )}
                        </div>

                        {/* Actions admin */}
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          {balanceData.account_blocked && (
                            <button onClick={handleUnblock} style={{
                              flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                              background: '#059669', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>🔓 Débloquer</button>
                          )}
                          <button onClick={() => setShowAdjustForm(!showAdjustForm)} style={{
                            flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                            background: '#F54119', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                          }}>🔧 Ajustement</button>
                          {balanceData.current_balance > 0 && (
                            <button onClick={handleManualPayout} style={{
                              flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                              background: '#FFB900', color: 'white', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem'
                            }}>💸 Virement</button>
                          )}
                        </div>

                        {/* Formulaire ajustement */}
                        {showAdjustForm && (
                          <div style={{ background: '#F4F5F7', padding: '16px', borderRadius: '10px', marginBottom: '16px' }}>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <select value={adjustDirection} onChange={e => setAdjustDirection(e.target.value)}
                                style={{ padding: '8px', borderRadius: '6px', border: '1px solid #DCE0E6', fontSize: '0.85rem' }}>
                                <option value="credit">+ Crédit</option>
                                <option value="debit">- Débit</option>
                              </select>
                              <input type="number" placeholder="Montant CDF" value={adjustAmount}
                                onChange={e => setAdjustAmount(e.target.value)}
                                style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #DCE0E6', fontSize: '0.85rem' }} />
                            </div>
                            <input type="text" placeholder="Note (optionnel)" value={adjustNote}
                              onChange={e => setAdjustNote(e.target.value)}
                              style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #DCE0E6', fontSize: '0.85rem', marginBottom: '8px', boxSizing: 'border-box' }} />
                            <button onClick={handleAdjust} style={{
                              width: '100%', padding: '10px', borderRadius: '6px', border: 'none',
                              background: '#F54119', color: 'white', fontWeight: '600', cursor: 'pointer'
                            }}>Appliquer l'ajustement</button>
                          </div>
                        )}

                        {/* Résumé financier */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                          <div style={{ padding: '12px', background: '#ECFDF5', borderRadius: '8px', borderLeft: '4px solid #10b981' }}>
                            <p style={{ fontSize: '0.75rem', color: '#5C636E' }}>Total gagné (tout)</p>
                            <p style={{ fontSize: '1.1rem', fontWeight: '700', color: '#059669' }}>
                              {(balanceData.total_earned_all_time || 0).toLocaleString('fr-FR')} CDF
                            </p>
                          </div>
                          <div style={{ padding: '12px', background: '#fff7ed', borderRadius: '8px', borderLeft: '4px solid #FFB900' }}>
                            <p style={{ fontSize: '0.75rem', color: '#5C636E' }}>Commissions (tout)</p>
                            <p style={{ fontSize: '1.1rem', fontWeight: '700', color: '#FF9B00' }}>
                              {(balanceData.total_commission_all_time || 0).toLocaleString('fr-FR')} CDF
                            </p>
                          </div>
                        </div>

                        {/* Filtre période */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', marginBottom: '12px' }}>
                          {[
                            { key: 'week', label: 'Semaine' },
                            { key: 'month', label: 'Mois' },
                            { key: 'year', label: 'Année' },
                            { key: 'all', label: 'Tout' }
                          ].map(p => (
                            <button key={p.key} onClick={() => setBalancePeriod(p.key)} style={{
                              padding: '8px 4px', border: 'none', borderRadius: '6px', fontSize: '0.8rem',
                              fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                              background: balancePeriod === p.key ? '#C9350F' : '#E9EBEF',
                              color: balancePeriod === p.key ? 'white' : '#5C636E'
                            }}>{p.label}</button>
                          ))}
                        </div>

                        {/* Résumé période */}
                        {balanceRides?.summary && (
                          <div style={{ background: '#FFFFFF', borderRadius: '10px', padding: '12px', marginBottom: '12px', border: '1px solid #E9EBEF' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', textAlign: 'center' }}>
                              <div>
                                <p style={{ fontSize: '0.7rem', color: '#5C636E' }}>Courses</p>
                                <p style={{ fontSize: '1.2rem', fontWeight: '700', color: '#C9350F' }}>{balanceRides.summary.rides_count}</p>
                              </div>
                              <div>
                                <p style={{ fontSize: '0.7rem', color: '#5C636E' }}>Chauffeur</p>
                                <p style={{ fontSize: '1.2rem', fontWeight: '700', color: '#059669' }}>{(balanceRides.summary.total_driver_earnings || 0).toLocaleString('fr-FR')}</p>
                              </div>
                              <div>
                                <p style={{ fontSize: '0.7rem', color: '#5C636E' }}>Commission</p>
                                <p style={{ fontSize: '1.2rem', fontWeight: '700', color: '#FF9B00' }}>{(balanceRides.summary.total_commission || 0).toLocaleString('fr-FR')}</p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Liste des courses */}
                        {balanceRides?.rides && balanceRides.rides.length > 0 ? (
                          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {balanceRides.rides.map((r, idx) => (
                              <div key={idx} style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                padding: '10px 12px', borderBottom: '1px solid #F4F5F7', fontSize: '0.82rem'
                              }}>
                                <div>
                                  <div style={{ fontWeight: '600', color: '#1C1C1C' }}>
                                    {r.ride_type?.charAt(0).toUpperCase() + r.ride_type?.slice(1)} — {r.payment_method === 'mobile_money' ? '📱 MM' : '💵 Cash'}
                                  </div>
                                  <div style={{ color: '#9AA0A8', fontSize: '0.75rem' }}>
                                    {new Date(r.created_at).toLocaleDateString('fr-FR')} {new Date(r.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <div style={{ fontWeight: '600', color: '#059669' }}>
                                    {(r.driver_earnings || 0).toLocaleString('fr-FR')} CDF
                                  </div>
                                  <div style={{ color: '#FF9B00', fontSize: '0.75rem' }}>
                                    -{(r.commission_base + r.commission_night).toLocaleString('fr-FR')} comm.
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p style={{ textAlign: 'center', color: '#9AA0A8', padding: '1rem', fontSize: '0.85rem' }}>
                            Aucune course sur cette période
                          </p>
                        )}
                      </>
                    ) : (
                      <p style={{ textAlign: 'center', color: '#9AA0A8', padding: '1rem' }}>Aucune donnée de solde</p>
                    )}
                  </div>
                )}
                  </>
                )}

                {/* Bouton Évaluations */}
                <button
                  onClick={() => setShowRatingsTab(!showRatingsTab)}
                  style={{
                    padding: '12px 20px', backgroundColor: showRatingsTab ? '#FF9B00' : '#FFB900', color: 'white',
                    border: 'none', borderRadius: '8px', fontWeight: '600',
                    cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s', width: '100%', marginBottom: '8px'
                  }}
                >
                  ⭐ {showRatingsTab ? 'Masquer' : 'Voir'} Évaluations
                </button>

                {showRatingsTab && (
                  <div style={{ marginTop: '12px', marginBottom: '16px' }}>
                    <TargetReviews type="driver" id={driver.driver_id} profile={driver} />
                  </div>
                )}

                <button
                  onClick={() => {
                    setResetPasswordUser({ ...driver, user_id: driver.driver_id, _isDriver: true });
                    setResetPasswordResult(null);
                    setRevokeSessionsState(null); setShowResetPasswordModal(true);
                    onClose();
                  }}
                  style={{
                    padding: '10px 20px', backgroundColor: '#dc2626', color: 'white',
                    border: 'none', borderRadius: '6px', fontWeight: '600',
                    cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s', width: '100%'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#B91C1C'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
                >
                  🔑 Mot de passe et appareils du chauffeur
                </button>

                {/* Zone dangereuse — Suspendre */}
                <div style={{ marginTop: '1rem', padding: '1rem', background: '#fef9ec', border: '1px solid #FFB900', borderRadius: '10px' }}>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', fontWeight: '700', color: '#C9350F', textTransform: 'uppercase' }}>
                    ⚠️ Zone de suspension
                  </p>
                  <button
                    onClick={async () => {
                      const reason = window.prompt('Raison de la suspension (optionnel) :');
                      if (reason === null) return;
                      if (!window.confirm(`Suspendre le compte de ${driver.name} ?`)) return;
                      const res = await onSuspend(driver.driver_id, reason);
                      if (!res?.success) alert('❌ ' + (res?.error || 'Erreur'));
                      else { alert('✅ Compte suspendu.'); onClose(); }
                    }}
                    style={{ width: '100%', padding: '0.65rem', background: '#FFB900', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.9rem' }}
                    onMouseOver={e => e.currentTarget.style.background = '#FF9B00'}
                    onMouseOut={e => e.currentTarget.style.background = '#FFB900'}
                  >
                    🚫 Suspendre ce chauffeur
                  </button>
                </div>
              </div>
            )}

            {/* ── Chauffeur SUSPENDU ── */}
            {driver.status === 'suspended' && (
              <div style={{ padding: '1rem 0', borderTop: '1px solid #E9EBEF', marginTop: '1rem' }}>
                <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', marginBottom: '0.75rem' }}>
                  <p style={{ margin: '0 0 0.5rem 0', fontWeight: '700', color: '#991b1b', fontSize: '0.9rem' }}>
                    🚫 Ce chauffeur est suspendu
                  </p>
                  {driver.suspension_reason && (
                    <p style={{ margin: 0, color: '#B91C1C', fontSize: '0.85rem' }}>
                      Raison : {driver.suspension_reason}
                    </p>
                  )}
                </div>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Réactiver le compte de ${driver.name} ?`)) return;
                    const res = await onReactivate(driver.driver_id);
                    if (!res?.success) alert('❌ ' + (res?.error || 'Erreur'));
                    else { alert('✅ Compte réactivé avec succès.'); onClose(); }
                  }}
                  style={{ width: '100%', padding: '0.75rem', background: '#059669', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem' }}
                  onMouseOver={e => e.currentTarget.style.background = '#047857'}
                  onMouseOut={e => e.currentTarget.style.background = '#059669'}
                >
                  ✅ Réactiver ce chauffeur
                </button>
                <button
                  onClick={async () => {
                    setResetPasswordUser({ ...driver, user_id: driver.driver_id, _isDriver: true });
                    setResetPasswordResult(null);
                    setRevokeSessionsState(null); setShowResetPasswordModal(true);
                    onClose();
                  }}
                  style={{ width: '100%', padding: '0.65rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.875rem', marginTop: '0.5rem' }}
                  onMouseOver={e => e.currentTarget.style.background = '#B91C1C'}
                  onMouseOut={e => e.currentTarget.style.background = '#dc2626'}
                >
                  🔑 Mot de passe et appareils
                </button>
              </div>
            )}

            {showRejectForm && (
              <div className="reject-form">
                <label>Raison du rejet (optionnel)</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Documents illisibles, informations incorrectes, etc."
                  rows="4"
                />
                <div className="modal-actions">
                  <button className="btn-secondary" onClick={() => setShowRejectForm(false)}>
                    Annuler
                  </button>
                  <button 
                    className="btn-reject" 
                    onClick={() => onReject(driver.driver_id, rejectionReason)}
                  >
                    Confirmer le rejet
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };



  // ==================== CLIENT IDENTITY VALIDATION ====================



  // ==================== CLIENT BALANCE MANAGEMENT ====================



  // ==================== PAGES DASHBOARD ====================



  // Handler pour réinitialiser le mot de passe d'un utilisateur ou chauffeur
  const handleResetUserPassword = async (userId) => {
    setResetPasswordLoading(true);
    setResetPasswordResult(null);
    try {
      // Déterminer si c'est un chauffeur ou un abonné
      const isDriver = resetPasswordUser?._isDriver;
      const endpoint = isDriver 
        ? `/admin/drivers/${userId}/reset-password`
        : `/admin/users/${userId}/reset-password`;
      
      const data = await apiService.post(endpoint, {}, { userType: 'admin' });
      if (data.success) {
        setResetPasswordResult({
          success: true,
          temporaryPassword: data.temporaryPassword,
          user: data.user || data.driver
        });
      }
    } catch (error) {
      console.error('Erreur reset password:', error);
      setResetPasswordResult({
        success: false,
        error: error.message || 'Erreur lors de la réinitialisation'
      });
    } finally {
      setResetPasswordLoading(false);
    }
  };



  // V4 sécurité : coupe toutes les connexions ouvertes d'un client ou d'un chauffeur
  const handleRevokeSessions = async (target) => {
    if (!target?.user_id) return;
    setRevokeSessionsState('loading');
    try {
      const endpoint = target._isDriver
        ? `/admin/drivers/${target.user_id}/revoke-sessions`
        : `/admin/users/${target.user_id}/revoke-sessions`;
      const data = await apiService.post(endpoint, {}, { userType: 'admin' });
      setRevokeSessionsState({ ok: true, text: data.message || 'Toutes les connexions ont été coupées' });
    } catch (error) {
      setRevokeSessionsState({ ok: false, text: error.message || 'Échec de la déconnexion' });
    }
  };

  // Modal pour afficher les détails d'un client/abonné
  const ClientDetailsModal = ({ subscription, onClose, onViewTrips, initialEdit = false }) => {
    const [clientStats, setClientStats] = useState({ today: 0, week: 0, month: 0, year: 0, total: 0 });
    const [loadingStats, setLoadingStats] = useState(true);
    const [showClientRatingsTab, setShowClientRatingsTab] = useState(false);

    // ── Édition des informations utilisateur (nom, email, téléphone, ville) ──
    const [editMode, setEditMode] = useState(!!initialEdit);
    const [savingInfo, setSavingInfo] = useState(false);
    const [infoError, setInfoError] = useState('');
    const [cityList, setCityList] = useState([]);
    const [infoForm, setInfoForm] = useState({ name: '', email: '', phone: '', city_slug: '' });

    React.useEffect(() => {
      const u = subscription?.user || {};
      setInfoForm({ name: u.name || '', email: u.email || '', phone: u.phone || '', city_slug: u.city_slug || '' });
      setEditMode(!!initialEdit);
      setInfoError('');
    }, [subscription?.user?.user_id]);

    React.useEffect(() => {
      apiService.get('/admin/my-cities', { userType: 'admin' }) // villes du périmètre de l'admin (superadmin : toutes)
        .then(r => { if (r?.success) setCityList(r.cities || r.data || []); })
        .catch(() => {});
    }, []);

    const cityLabel = (slug) => (cityList.find(c => c.slug === slug)?.name) || (slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'Non renseigné');

    const saveUserInfo = async () => {
      const uid = subscription?.user?.user_id;
      if (!uid) { setInfoError('Utilisateur introuvable'); return; }
      if (!infoForm.name.trim()) { setInfoError('Le nom est requis'); return; }
      setSavingInfo(true); setInfoError('');
      try {
        const res = await apiService.put(`/admin/users/${uid}`, {
          name: infoForm.name, phone: infoForm.phone, email: infoForm.email, city_slug: infoForm.city_slug,
        }, { userType: 'admin' });
        if (res?.success) {
          const nu = res.user || {};
          // Refléter immédiatement dans le modal.
          subscription.user.name = nu.name ?? infoForm.name;
          subscription.user.phone = nu.phone ?? infoForm.phone;
          subscription.user.email = nu.email ?? infoForm.email;
          subscription.user.city_slug = nu.city_slug ?? infoForm.city_slug;
          setEditMode(false);
        } else {
          setInfoError(res?.error || 'Échec de la mise à jour');
        }
      } catch (e) {
        setInfoError(e?.error || e?.data?.error || e?.message || 'Échec de la mise à jour');
      } finally {
        setSavingInfo(false);
      }
    };

    React.useEffect(() => {
      if (subscription?.user?.user_id) {
        const fetchClientStats = async () => {
          try {
            const data = await apiService.get(`/scans/user/${subscription.user.user_id}/stats`, { userType: 'admin' });
            if (data.success && data.stats) {
              setClientStats(data.stats);
            }
          } catch (error) {
            console.error('Erreur récupération stats client:', error);
          } finally {
            setLoadingStats(false);
          }
        };
        fetchClientStats();
      }
    }, [subscription?.user?.user_id]);


    if (!subscription) return null;

    const editLabelStyle = { display: 'block', fontSize: '0.72rem', color: '#5C636E', fontWeight: 600, margin: '6px 0 3px' };
    const editInputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #DCE0E6', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };

    const planKey = (subscription.plan_type || subscription.plan_name || '').toLowerCase();
    const plan = PLAN_DETAILS[planKey] || {
      name: subscription.plan_name || 'INCONNU',
      color: '#5C636E'
    };

    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Détails du client</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className="modal-body">
            <div className="info-section">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3>Informations personnelles</h3>
                {!editMode && subscription.user?.user_id && (
                  <button onClick={() => { setEditMode(true); setInfoError(''); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#C9350F', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                    <Edit2 size={14} /> Modifier
                  </button>
                )}
              </div>
              <div style={{display: 'flex', alignItems: 'flex-start', gap: '1.5rem', marginBottom: '1.5rem'}}>
                <div style={{
                  width: '100px',
                  height: '100px',
                  borderRadius: '50%',
                  background: subscription.user?.photo ? 'transparent' : plan.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  border: '3px solid #E9EBEF',
                  flexShrink: 0
                }}>
                  {subscription.user?.photo ? (
                    <Img src={subscription.user.photo} alt="Profil" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                  ) : (
                    <span style={{fontSize: '2.5rem'}}>👤</span>
                  )}
                </div>
                <div style={{flex: 1}}>
                  {editMode ? (
                    <>
                      <label style={editLabelStyle}>Nom</label>
                      <input style={editInputStyle} value={infoForm.name} onChange={e => setInfoForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom complet" />
                      <label style={editLabelStyle}>Email</label>
                      <input style={editInputStyle} value={infoForm.email} onChange={e => setInfoForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemple.com" type="email" />
                    </>
                  ) : (
                    <>
                      <p style={{fontSize: '1.2rem', fontWeight: '600', marginBottom: '0.5rem'}}>{subscription.user?.name || 'N/A'}</p>
                      <p style={{color: '#666', fontSize: '0.9rem'}}>{subscription.user?.email || 'Aucun email'}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <label>Téléphone</label>
                  {editMode ? (
                    <input style={editInputStyle} value={infoForm.phone} onChange={e => setInfoForm(f => ({ ...f, phone: e.target.value }))} placeholder="+243..." />
                  ) : (
                    <p>{subscription.user?.phone || 'N/A'}</p>
                  )}
                </div>
                <div className="info-item">
                  <label>Ville</label>
                  {editMode ? (
                    <select style={editInputStyle} value={infoForm.city_slug} onChange={e => setInfoForm(f => ({ ...f, city_slug: e.target.value }))}>
                      <option value="">— Choisir —</option>
                      {cityList.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                    </select>
                  ) : (
                    <p>{cityLabel(subscription.user?.city_slug)}</p>
                  )}
                </div>
                <div className="info-item">
                  <label>Date de naissance</label>
                  <p>{subscription.user?.dateOfBirth ? new Date(subscription.user.dateOfBirth).toLocaleDateString('fr-FR') : 'Non renseigné'}</p>
                </div>
                <div className="info-item">
                  <label>Date d'inscription</label>
                  <p>{subscription.user?.created_at ? new Date(subscription.user.created_at).toLocaleDateString('fr-FR') : 'N/A'}</p>
                </div>
              </div>

              {editMode && (
                <div style={{ marginTop: '1rem' }}>
                  {infoError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px 12px', borderRadius: 8, marginBottom: 10, fontSize: '0.85rem', fontWeight: 600 }}>{infoError}</div>}
                  <div style={{ fontSize: '0.78rem', color: '#C9350F', background: '#fef3c7', padding: '8px 12px', borderRadius: 8, marginBottom: 10 }}>
                    ⚠️ Le téléphone sert d'identifiant de connexion — le modifier change la façon dont l'utilisateur se connecte (apps client & fournisseur).
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={saveUserInfo} disabled={savingInfo}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 700, cursor: savingInfo ? 'not-allowed' : 'pointer', opacity: savingInfo ? 0.6 : 1 }}>
                      <Save size={15} /> {savingInfo ? 'Enregistrement…' : 'Enregistrer'}
                    </button>
                    <button onClick={() => { const u = subscription?.user || {}; setInfoForm({ name: u.name || '', email: u.email || '', phone: u.phone || '', city_slug: u.city_slug || '' }); setEditMode(false); setInfoError(''); }} disabled={savingInfo}
                      style={{ background: '#E9EBEF', color: '#414852', border: 'none', borderRadius: 8, padding: '9px 16px', fontWeight: 600, cursor: 'pointer' }}>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="info-section">
              <h3>Abonnement</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>Plan</label>
                  <p style={{color: plan.color, fontWeight: '600'}}>{plan.name}</p>
                </div>
                <div className="info-item">
                  <label>Prix</label>
                  <p style={{color: plan.color, fontWeight: '600'}}>{subscription.price ? `${subscription.price.toLocaleString()} FC` : 'N/A'}</p>
                </div>
                <div className="info-item">
                  <label>Statut</label>
                  <p style={{color: subscription.status === 'active' ? '#10b981' : '#DC2626', fontWeight: '600'}}>
                    {subscription.status === 'active' ? '✅ Actif' : '⏱️ Expiré'}
                  </p>
                </div>
                <div className="info-item">
                  <label>Valide jusqu'au</label>
                  <p>{subscription.valid_until ? new Date(subscription.valid_until).toLocaleDateString('fr-FR') : 'N/A'}</p>
                </div>
              </div>
            </div>

            {!loadingStats && (
              <>
                <div className="info-section">
                  <h3>📊 Trajets Valides (Filtre par période)</h3>
                  <div style={{marginBottom: '12px'}}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '16px' }}>
                      <button
                        onClick={() => setClientStats({...clientStats, filterPeriod: 'today'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (clientStats.filterPeriod || 'month') === 'today' ? '#C9350F' : '#E9EBEF',
                          color: (clientStats.filterPeriod || 'month') === 'today' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Aujourd'hui
                      </button>
                      <button
                        onClick={() => setClientStats({...clientStats, filterPeriod: 'week'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (clientStats.filterPeriod || 'month') === 'week' ? '#C9350F' : '#E9EBEF',
                          color: (clientStats.filterPeriod || 'month') === 'week' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Semaine
                      </button>
                      <button
                        onClick={() => setClientStats({...clientStats, filterPeriod: 'month'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (clientStats.filterPeriod || 'month') === 'month' ? '#C9350F' : '#E9EBEF',
                          color: (clientStats.filterPeriod || 'month') === 'month' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Mois
                      </button>
                      <button
                        onClick={() => setClientStats({...clientStats, filterPeriod: 'year'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (clientStats.filterPeriod || 'month') === 'year' ? '#C9350F' : '#E9EBEF',
                          color: (clientStats.filterPeriod || 'month') === 'year' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Année
                      </button>
                      <button
                        onClick={() => setClientStats({...clientStats, filterPeriod: 'total'})}
                        style={{
                          padding: '10px 8px',
                          border: 'none',
                          borderRadius: '6px',
                          backgroundColor: (clientStats.filterPeriod || 'month') === 'total' ? '#C9350F' : '#E9EBEF',
                          color: (clientStats.filterPeriod || 'month') === 'total' ? 'white' : '#5C636E',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '12px',
                          transition: 'all 0.2s'
                        }}
                      >
                        Total
                      </button>
                    </div>
                  </div>
                  <div style={{padding: '2rem', background: 'linear-gradient(135deg, #FEE9E2 0%, #FEE9E2 100%)', borderRadius: '12px', textAlign: 'center', border: '2px solid #C9350F'}}>
                    <p style={{fontSize: '0.95rem', color: '#C9350F', marginBottom: '0.5rem', fontWeight: '500'}}>
                      {(clientStats.filterPeriod || 'month') === 'today' && 'Trajets valides aujourd\'hui'}
                      {(clientStats.filterPeriod || 'month') === 'week' && 'Trajets valides cette semaine'}
                      {(clientStats.filterPeriod || 'month') === 'month' && 'Trajets valides ce mois'}
                      {(clientStats.filterPeriod || 'month') === 'year' && 'Trajets valides cette année'}
                      {(clientStats.filterPeriod || 'month') === 'total' && 'Trajets valides (total depuis le début)'}
                    </p>
                    <p style={{fontSize: '2.5rem', fontWeight: '700', color: '#C9350F', margin: '0.5rem 0'}}>
                      {(clientStats.filterPeriod || 'month') === 'today' && clientStats.today}
                      {(clientStats.filterPeriod || 'month') === 'week' && clientStats.week}
                      {(clientStats.filterPeriod || 'month') === 'month' && clientStats.month}
                      {(clientStats.filterPeriod || 'month') === 'year' && clientStats.year}
                      {(clientStats.filterPeriod || 'month') === 'total' && clientStats.total}
                    </p>
                  </div>
                </div>

                <div className="info-section">
                  <h3>📈 Engagement & Utilisation</h3>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                    <div style={{padding: '1rem', background: '#FFFFFF', borderRadius: '8px', borderLeft: '4px solid #10B981'}}>
                      <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>Trajets/jour (mois)</p>
                      <p style={{fontSize: '1.3rem', fontWeight: '600', color: '#10B981'}}>
                        {clientStats.month > 0 ? (clientStats.month / 30).toFixed(1) : 0}
                      </p>
                    </div>
                    <div style={{padding: '1rem', background: '#FFFFFF', borderRadius: '8px', borderLeft: '4px solid #FFB900'}}>
                      <p style={{fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem'}}>Jours d'utilisation (mois)</p>
                      <p style={{fontSize: '1.3rem', fontWeight: '600', color: '#FFB900'}}>
                        {clientStats.month > 0 ? Math.ceil(clientStats.month / 3) : 0} j
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Section Évaluations client */}
          <div style={{ padding: '0 1.5rem 1rem' }}>
            <button
              onClick={() => setShowClientRatingsTab(!showClientRatingsTab)}
              style={{
                padding: '12px 20px', backgroundColor: showClientRatingsTab ? '#FF9B00' : '#FFB900', color: 'white',
                border: 'none', borderRadius: '8px', fontWeight: '600',
                cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s', width: '100%', marginBottom: '8px'
              }}
            >
              ⭐ {showClientRatingsTab ? 'Masquer' : 'Voir'} Évaluations reçues
            </button>

            {showClientRatingsTab && (
              <div style={{ marginTop: '8px' }}>
                <TargetReviews type="client" id={subscription.user.user_id} profile={subscription.user} />
              </div>
            )}
          </div>

          <div style={{
            padding: '1.5rem',
            borderTop: '1px solid #E9EBEF',
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                setResetPasswordUser(subscription.user);
                setResetPasswordResult(null);
                setRevokeSessionsState(null); setShowResetPasswordModal(true);
                onClose();
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#B91C1C'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
            >
              🔑 Mot de passe et appareils
            </button>
            <button
              onClick={() => {
                if (onViewTrips && subscription.user) {
                  onViewTrips(subscription.user.user_id, subscription.user.name);
                  onClose();
                }
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#C9350F',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#F54119'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#C9350F'}
            >
              📊 Historique des trajets
            </button>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#E9EBEF',
                color: '#414852',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.backgroundColor = '#DCE0E6'}
              onMouseOut={(e) => e.target.style.backgroundColor = '#E9EBEF'}
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  };

  // AdminClientTripHistory component
  const AdminClientTripHistory = ({ userId, clientName, onBack }) => {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const fetchTrips = useCallback(async () => {
      try {
        setLoading(true);
        const response = await apiService.get(
          `/trips/passenger/${userId}?filter=${filter}&limit=100`,
          { userType: 'admin' }
        );
        if (response.success) {
          setTrips(response.trips || []);
        }
      } catch (error) {
        console.error('Erreur chargement trajets du client:', error);
      } finally {
        setLoading(false);
      }
    }, [userId, filter]);

    useEffect(() => {
      fetchTrips();
    }, [fetchTrips]);

    const formatDate = (date) => {
      if (!date) return 'N/A';
      const d = new Date(date);
      return d.toLocaleDateString('fr-FR', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const getStatusColor = (status) => {
      switch(status) {
        case 'completed': return '#10b981';
        case 'cancelled': return '#DC2626';
        default: return '#5C636E';
      }
    };

    const getStatusLabel = (status) => {
      switch(status) {
        case 'completed': return '✅ Complété';
        case 'cancelled': return '❌ Annulé';
        default: return '⏳ En cours';
      }
    };

    return (
      <div className="dashboard-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">Historique des trajets - {clientName}</h1>
            <p style={{color: '#666', marginTop: '0.5rem', fontSize: '0.95rem'}}>Liste complète des trajets du client</p>
          </div>
          <button
            onClick={onBack}
            style={{
              padding: '10px 20px',
              backgroundColor: '#F4F5F7',
              color: '#414852',
              border: '1px solid #DCE0E6',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            ← Retour aux clients
          </button>
        </div>

        <div style={{marginBottom: '2rem'}}>
          <div style={{display: 'flex', gap: '12px'}}>
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: '8px 16px',
                backgroundColor: filter === 'all' ? '#C9350F' : '#E9EBEF',
                color: filter === 'all' ? 'white' : '#5C636E',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Tous
            </button>
            <button
              onClick={() => setFilter('completed')}
              style={{
                padding: '8px 16px',
                backgroundColor: filter === 'completed' ? '#10b981' : '#E9EBEF',
                color: filter === 'completed' ? 'white' : '#5C636E',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Complétés
            </button>
            <button
              onClick={() => setFilter('cancelled')}
              style={{
                padding: '8px 16px',
                backgroundColor: filter === 'cancelled' ? '#DC2626' : '#E9EBEF',
                color: filter === 'cancelled' ? 'white' : '#5C636E',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Annulés
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{textAlign: 'center', padding: '2rem', color: '#666'}}>
            <p>Chargement des trajets...</p>
          </div>
        ) : trips.length === 0 ? (
          <div style={{textAlign: 'center', padding: '2rem', background: '#FFFFFF', borderRadius: '8px', color: '#666'}}>
            <p>Aucun trajet trouvé</p>
          </div>
        ) : (
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1rem'}}>
            {trips.map((trip) => (
              <div 
                key={trip.trip_id}
                style={{
                  background: 'white',
                  border: '1px solid #E9EBEF',
                  borderRadius: '8px',
                  padding: '1rem',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #F4F5F7'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start'}}>
                    <div>
                      <h3 style={{margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600'}}>
                        {trip.route_name || 'Trajet'}
                      </h3>
                      <p style={{margin: '0', fontSize: '0.85rem', color: '#666'}}>
                        {formatDate(trip.start_time)}
                      </p>
                    </div>
                    <span style={{
                      padding: '4px 12px',
                      background: getStatusColor(trip.status),
                      color: 'white',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {getStatusLabel(trip.status)}
                    </span>
                  </div>
                </div>

                <div style={{marginBottom: '1rem', fontSize: '0.9rem', color: '#414852'}}>
                  <p style={{margin: '0.5rem 0'}}>
                    <strong>🚕 Chauffeur:</strong> {trip.driver_name || 'N/A'}
                  </p>
                  {trip.start_location && (
                    <p style={{margin: '0.5rem 0'}}>
                      <strong>📍 Départ:</strong> {trip.start_location.name || `${trip.start_location.latitude.toFixed(4)}, ${trip.start_location.longitude.toFixed(4)}`}
                    </p>
                  )}
                  {trip.end_location && (
                    <p style={{margin: '0.5rem 0'}}>
                      <strong>🎯 Arrivée:</strong> {trip.end_location.name || `${trip.end_location.latitude.toFixed(4)}, ${trip.end_location.longitude.toFixed(4)}`}
                    </p>
                  )}
                  {trip.distance_km && (
                    <p style={{margin: '0.5rem 0'}}>
                      <strong>📏 Distance:</strong> {trip.distance_km} km
                    </p>
                  )}
                  {trip.duration_formatted && (
                    <p style={{margin: '0.5rem 0'}}>
                      <strong>⏱️ Durée:</strong> {trip.duration_formatted}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };


  // ==================== CHARGEMENT ARRÊTS & LIGNES ====================
  const loadStopsFromAPI = async () => {
    setLoadingStops(true);
    try {
      const response = await apiService.get('/stops', { userType: 'admin' });
      if (response.success && response.stops) {
        setStops(response.stops);
      }
    } catch (error) {
      console.error('Erreur chargement arrêts:', error);
    } finally {
      setLoadingStops(false);
    }
  };

  const loadRoutesFromAPI = async () => {
    setLoadingRoutes(true);
    try {
      const response = await apiService.get('/routes', { userType: 'admin' });
      if (response.success && response.routes) {
        setRoutes(response.routes);
      }
    } catch (error) {
      console.error('Erreur chargement lignes:', error);
    } finally {
      setLoadingRoutes(false);
    }
  };

  // ==================== PAGE: ARRÊTS ====================

  // ==================== PAGE: LIGNES (Routes) ====================


  // ========================================
  // TAXIS ADMIN PAGE
  // ========================================

  // Rendu de la page Revenue (fonction pure sans hooks - évite le remount infini)

  // ==================== RENDER ====================

  // Contexte partagé avec toutes les pages extraites
  const ctxValue = {
    // Navigation
    currentPage, setCurrentPage,
    // Auth
    isAuthenticated, setIsAuthenticated, currentAdmin, setCurrentAdmin,
    // UI
    menuOpen, setMenuOpen, showNotifications, setShowNotifications,
    showUserMenu, setShowUserMenu, errorMessage, setErrorMessage,
    // Drivers
    drivers, setDrivers, loadingDrivers, selectedDriver, setSelectedDriver,
    approveDriver, rejectDriver, suspendDriver, reactivateDriver,
    showEditDriverModal, setShowEditDriverModal, editingDriver, setEditingDriver,
    editDriverFormData, setEditDriverFormData, statusFilter, setStatusFilter,
    // Subscriptions / Abonnés
    subscriptions, setSubscriptions, loadingSubscriptions,
    searchText, setSearchText, planFilter, setPlanFilter,
    subscriptionFilter, setSubscriptionFilter,
    showEditSubscriptionModal, setShowEditSubscriptionModal,
    editingSubscription, setEditingSubscription, editFormData, setEditFormData,
    selectedSubscription, setSelectedSubscription,
    // Clients
    selectedClient, setSelectedClient,
    viewingClientTrips, setViewingClientTrips,
    viewingClientUserId, setViewingClientUserId, viewingClientName, setViewingClientName,
    // Plans prices
    editingPrices, setEditingPrices, planPrices, setPlanPrices,
    // Notifications
    notifications, setNotifications, unreadCount,
    // Stops & Routes
    stops, setStops, routes, setRoutes, loadingStops, loadingRoutes,
    showStopModal, setShowStopModal, selectedStop, setSelectedStop, stopForm, setStopForm,
    showRouteModal, setShowRouteModal, selectedRoute, setSelectedRoute, routeForm, setRouteForm,
    loadStopsFromAPI, loadRoutesFromAPI,
    // Revenue
    revenueSummary, revenueByPlan, revenueStats, loadingRevenue,
    revenueStartDate, setRevenueStartDate, revenueEndDate, setRevenueEndDate,
    revenueGroupBy, setRevenueGroupBy, revenueFilterPreset, setRevenueFilterPreset,
    revenueLocalStartDate, setRevenueLocalStartDate, revenueLocalEndDate, setRevenueLocalEndDate,
    handleRevenuePresetFilter, handleRevenueFilterApply, handleRevenueReset,
    loadRevenueFromAPI,
    // Misc
    loadDriversFromAPI, loadSubscriptionsFromAPI, loadNotificationsFromAPI,
    handleResetUserPassword,
    stats, sidebarOpen, setSidebarOpen,
    // Supplier
    selectedSupplierId, setSelectedSupplierId,
    // Password reset modal
    showResetPasswordModal, setShowResetPasswordModal,
    resetPasswordUser, setResetPasswordUser,
    resetPasswordResult, setResetPasswordResult,
    resetPasswordLoading, setResetPasswordLoading,
  };

  return (
    <AdminContext.Provider value={ctxValue}>
    <div className="admin-dashboard">

      {/* Sidebar */}
      {isAuthenticated && (
        <div className={`sidebar ${menuOpen ? 'open' : ''}`}>
          <div className="sidebar-logo">
            <Car size={32} />
            <h1>Mbiyo Admin</h1>
          </div>

          <div className="sidebar-nav">

            {/* ── helper local ── */}
            {(() => {
              const NavItem = ({ page, icon: Icon, label, badge, onClick: customClick }) => {
                if (!canAccessPage(page)) return null;
                return (
                  <div
                    className={`nav-item ${currentPage === page ? 'active' : ''}`}
                    onClick={customClick || (() => { setCurrentPage(page); setMenuOpen(false); })}
                  >
                    {Icon && <Icon size={18} />}
                    <span style={{ flex: 1 }}>{label}</span>
                    {badge > 0 && (
                      <span style={{ background: '#FFB900', color: 'white', padding: '0.2rem 0.45rem', borderRadius: '12px', fontSize: '0.7rem', fontWeight: '700' }}>
                        {badge}
                      </span>
                    )}
                  </div>
                );
              };

              const Section = ({ sKey, label, pages, children }) => {
                // Masquer toute la section si l'admin n'a accès à aucune de ses pages
                if (pages && !pages.some(canAccessPage)) return null;
                return (
                  <div style={{ marginBottom: '2px' }}>
                    <div
                      onClick={() => setSidebarOpen(prev => ({ ...prev, [sKey]: !prev[sKey] }))}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '0.6rem 1rem 0.35rem', cursor: 'pointer', userSelect: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '4px' }}
                    >
                      {sidebarOpen[sKey] ? <ChevronDown size={13} color="#9AA0A8" /> : <ChevronRight size={13} color="#9AA0A8" />}
                      <span style={{ fontSize: '0.6rem', textTransform: 'uppercase', fontWeight: '700', color: '#9AA0A8', letterSpacing: '0.1em' }}>{label}</span>
                    </div>
                    {sidebarOpen[sKey] && children}
                  </div>
                );
              };

              return (
                <>
                  {/* TABLEAU DE BORD (hors section) */}
                  <NavItem page="dashboard" icon={LayoutDashboard} label="Dashboard" />

                  {/* ── UTILISATEURS ── */}
                  <Section sKey="users" label="Utilisateurs" pages={['validation','client-validation','drivers','driver-promos','abonnes']}>
                    <NavItem page="validation"        icon={ShieldCheck}    label="Validation Chauffeurs" badge={stats.pendingDrivers || tabBadges['validation']} />
                    <NavItem page="client-validation" icon={UserCheck}      label="Validation Clients" badge={tabBadges['client-validation']} />
                    <NavItem page="drivers"           icon={Truck}          label="Chauffeurs" />
                    <NavItem page="driver-promos"     icon={Star}           label="Promotions chauffeurs" />
                    <NavItem page="abonnes"           icon={Star}           label="Utilisateurs" />
                  </Section>

                  {/* ── FINANCES ── */}
                  <Section sKey="finances" label="Finances" pages={['finance-pilotage','payments','revenue','soldes','subscriptions','client-soldes']}>
                    <NavItem page="finance-pilotage" icon={BarChart3}    label="Pilotage financier" />
                    <NavItem page="payments"      icon={CreditCard}   label="Paiements" badge={tabBadges['payments']} />
                    <NavItem page="revenue"       icon={TrendingUp}   label="Revenu d'abonnements" />
                    <NavItem page="soldes"        icon={Wallet}       label="Virements" />
                    <NavItem page="subscriptions" icon={Package}      label="Plans" />
                    <NavItem page="client-soldes" icon={Users}        label="Soldes" />
                  </Section>

                  {/* ── TRANSPORT ── */}
                  <Section sKey="transport" label="Transport" pages={['taxis','active-rides','online-drivers','stops','routes']}>
                    <NavItem page="taxis"  icon={MapPin}     label="Courses Taxi" />
                    <NavItem page="active-rides"   icon={Navigation} label="Courses actives" />
                    <NavItem page="online-drivers" icon={Truck}      label="Chauffeurs en ligne" />
                    <NavItem page="stops"  icon={Navigation} label="Arrêts" />
                    <NavItem page="routes" icon={Route}      label="Lignes" />
                  </Section>

                  {/* ── HÉBERGEMENT ── */}
                  <Section sKey="lodging" label="Hébergement" pages={['lodging-moderation','lodging-reviews']}>
                    <NavItem page="lodging-moderation" icon={Bed} label="Modération Annonces" />
                    <NavItem page="lodging-reviews" icon={Star} label="Modération Avis" />
                  </Section>

                  {/* ── SALLES & ESPACES ÉVÉNEMENTIELS ── */}
                  <Section sKey="venues" label="Salles" pages={['venues','venue-bookings']}>
                    <NavItem page="venues" icon={PartyPopper} label="Salles à valider" />
                    <NavItem page="venue-bookings" icon={Calendar} label="Réservations de salles" />
                  </Section>

                  {/* ── LIVRAISON ── */}
                  <Section sKey="delivery" label="Business" pages={['delivery-orders','delivery-suppliers','open-businesses','delivery-drivers','delivery-category-templates','store-templates','delivery-zones','delivery-stats']}>
                    <NavItem page="delivery-orders"    icon={ShoppingBag} label="Commandes Livraison" badge={tabBadges['delivery-orders']} />
                    <NavItem page="delivery-suppliers" icon={Store}       label="Fournisseurs"
                      onClick={() => { setCurrentPage('delivery-suppliers'); setSelectedSupplierId(null); setMenuOpen(false); }} />
                    <NavItem page="open-businesses" icon={Store} label="Business ouverts" />
                    <NavItem page="delivery-drivers"   icon={Bike}        label="Livreurs" />
                    <NavItem page="delivery-category-templates" icon={Layers} label="Modèles de rayons" />
                    <NavItem page="store-templates"    icon={Package}     label="Modèles de boutique" />
                    <NavItem page="delivery-zones"     icon={Map}         label="Zones de Livraison" />
                    <NavItem page="delivery-stats"     icon={BarChart2}   label="Stats Livraison" />
                  </Section>

                  {/* ── BEAUTÉ ── */}
                  <Section sKey="beauty" label="Beauté" pages={['beauty-bookings']}>
                    <NavItem page="beauty-bookings" icon={Store} label="Réservations Beauté" badge={tabBadges['beauty-bookings']} />
                  </Section>

                  {/* ── BILLETTERIE ── */}
                  <Section sKey="events" label="Billetterie" pages={['events']}>
                    <NavItem page="events" icon={Ticket} label="Événements" badge={tabBadges['events']} />
                  </Section>

                  {/* ── PLATEFORME ── */}
                  <Section sKey="platform" label="Plateforme" pages={['security','ratings','messages','whatsapp','support','platform-settings','app-releases','cities']}>
                    <NavItem page="security"           icon={ShieldCheck}   label="Centre de sécurité" />
                    <NavItem page="ratings"            icon={Star}          label="Avis" />
                    <NavItem page="messages"           icon={Mail}          label="Messages" />
                    <NavItem page="whatsapp"           icon={MessageCircle} label="WhatsApp" badge={waUnread || undefined} />
                    <NavItem page="support"            icon={LifeBuoy}      label="Support" badge={tabBadges['support']} />
                    <NavItem page="platform-settings"  icon={Settings}      label="Paramètres" />
                    <NavItem page="app-releases"       icon={Smartphone}    label="Applications" />
                    <NavItem page="cities"             icon={Globe}         label="Villes" />
                  </Section>

                  {/* ── ADMINISTRATION (superadmin ; IT : maintenance + journal en lecture) ── */}
                  <Section sKey="administration" label="Administration" pages={['maintenance','errors','admins','audit-logs']}>
                    <NavItem page="maintenance" icon={Wrench}      label="Maintenance" />
                    <NavItem page="errors"      icon={Bug}         label="Erreurs" />
                    <NavItem page="admins"      icon={Users}       label="Comptes & Rôles" />
                    <NavItem page="audit-logs"  icon={ShieldCheck} label="Journal d'audit" />
                  </Section>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Topbar */}
      {isAuthenticated && (
        <div className="topbar">
          <div className="topbar-left">
            <button className="icon-btn" onClick={() => setMenuOpen(!menuOpen)}>
              <Menu size={24} />
            </button>
          </div>

          <div className="topbar-right">
            <div style={{ position: 'relative' }}>
              <button 
                className="icon-btn" 
                onClick={() => {
                  setShowNotifications(!showNotifications);
                  setShowUserMenu(false);
                }}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    width: '8px',
                    height: '8px',
                    background: '#DC2626',
                    borderRadius: '50%',
                  }}></span>
                )}
              </button>
              {showNotifications && <Stable render={NotificationsPanel} key="NotificationsPanel" />}
            </div>

            <button 
              className="icon-btn"
              onClick={() => {
                setCurrentPage('settings');
                setShowNotifications(false);
                setShowUserMenu(false);
              }}
            >
              <Settings size={20} />
            </button>

            <div style={{ position: 'relative' }}>
              <div 
                className="user-menu" 
                onClick={() => {
                  setShowUserMenu(!showUserMenu);
                  setShowNotifications(false);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div className="user-avatar">{currentAdmin?.name?.charAt(0) || 'A'}</div>
                <span>{currentAdmin?.name || 'Admin'}</span>
              </div>
              {showUserMenu && <Stable render={UserMenuPanel} key="UserMenuPanel" />}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="main-content">
        {/* Bannière notifications push — visible tant que la permission n'est pas accordée */}
        {pushPermission !== 'granted' && pushPermission !== 'unsupported' && !pushDismissed && (
          <div className="push-banner">
            <Bell size={20} />
            <span className="push-banner-text">
              {pushPermission === 'denied'
                ? 'Les notifications push sont bloquées par le navigateur. Autorisez-les dans les réglages du site pour être alerté des nouveaux chauffeurs, paiements, etc.'
                : 'Activez les notifications push pour être alerté en temps réel (nouveaux chauffeurs, paiements, messages…).'}
            </span>
            {pushPermission === 'default' && (
              <button className="push-banner-btn" onClick={enablePush}>Activer</button>
            )}
            <button className="push-banner-close" onClick={() => setPushDismissed(true)} aria-label="Fermer"><X size={16} /></button>
          </div>
        )}
        {/* Error Message Banner */}
        {errorMessage && (
          <div className="error-banner">
            <AlertCircle size={20} />
            <span>{errorMessage}</span>
            <button className="error-close" onClick={() => setErrorMessage('')}>
              <X size={18} />
            </button>
          </div>
        )}
        
        {viewingClientTrips && viewingClientUserId ? (
          <Stable render={AdminClientTripHistory} key="AdminClientTripHistory" 
            userId={viewingClientUserId}
            clientName={viewingClientName}
            onBack={() => setViewingClientTrips(false)}
          />
        ) : null}

        {currentPage === 'login' && <LoginPage />}
        {currentPage === 'register' && <RegisterPage />}
        {currentPage === 'settings' && <SettingsPage />}
        {currentPage === 'dashboard' && <DashboardPage />}
        {currentPage === 'validation' && <DriverValidationPage />}
        {currentPage === 'driver-promos' && <DriverPromosPage />}
        {currentPage === 'client-validation' && <ClientValidationPage />}
        {currentPage === 'client-soldes' && <ClientBalancePage />}
        {currentPage === 'subscriptions' && <PlansPage />}
        {currentPage === 'abonnes' && <SubscribersPage />}
        {currentPage === 'drivers' && <DriversPage />}
        {currentPage === 'stops' && <StopsPage />}
        {currentPage === 'routes' && <LinesPage />}
        {currentPage === 'ratings' && <ReviewsPage />}
        {currentPage === 'messages' && <MessagesPage />}
        {currentPage === 'payments' && <PaymentsPage />}
        {currentPage === 'revenue' && <RevenuePage />}
        {currentPage === 'finance-pilotage' && <FinancePilotagePage />}
        {currentPage === 'taxis' && <TaxiRidesPage />}
        {currentPage === 'active-rides' && <ActiveRidesPage />}
        {currentPage === 'online-drivers' && <OnlineDriversPage />}
        {currentPage === 'open-businesses' && <OpenBusinessesPage />}
        {currentPage === 'soldes' && <BalancesTransfersPage />}
        {currentPage === 'security' && <SecurityCenterPage />}
        {currentPage === 'maintenance' && <MaintenancePage />}
        {currentPage === 'errors' && <ErrorsPage />}
        {currentPage === 'whatsapp' && <WhatsAppPage />}
        {currentPage === 'platform-settings' && <PlatformSettingsPage />}
        {currentPage === 'app-releases' && <AppReleasesPage />}
        {currentPage === 'delivery-suppliers' && !selectedSupplierId && (
          <DeliverySuppliersPage onSelectSupplier={(id) => setSelectedSupplierId(id)} onEditOwner={openUserEditor} />
        )}
        {currentPage === 'delivery-suppliers' && selectedSupplierId && (
          <SupplierDetailPage
            supplierId={selectedSupplierId}
            onBack={() => setSelectedSupplierId(null)}
            onEditOwner={openUserEditor}
          />
        )}
        {currentPage === 'lodging-moderation' && <LodgingModerationPage />}
        {currentPage === 'venues' && <VenuesAdminPage />}
        {currentPage === 'venue-bookings' && <VenueBookingsAdminPage />}
        {currentPage === 'lodging-reviews' && <ReviewsPage preset={{ target_type: 'lodging' }} title="Avis hébergement" />}
        {currentPage === 'admins' && <AdminAccountsPage />}
        {currentPage === 'audit-logs' && <AdminAuditLogPage />}
        {currentPage === 'delivery-orders' && <DeliveryOrdersPage />}
        {currentPage === 'beauty-bookings' && <BeautyBookingsPage />}
        {currentPage === 'events' && <EventsAdminPage />}
        {currentPage === 'delivery-zones' && <DeliveryZonesPage />}
        {currentPage === 'delivery-drivers' && <DeliveryDriversPage />}
        {currentPage === 'delivery-category-templates' && <CategoryTemplatesPage />}
        {currentPage === 'store-templates' && <StoreTemplatesPage />}
        {currentPage === 'support' && <SupportPage />}
        {currentPage === 'delivery-stats' && <DeliveryStatsPage />}
        {currentPage === 'cities' && <CitiesPage />}
      </div>

      {/* Modals */}

      {/* Modal de réinitialisation du mot de passe */}
      {showResetPasswordModal && resetPasswordUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); setRevokeSessionsState(null); }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem'}}>
              <h2 style={{margin: 0, fontSize: '1.3rem', color: '#1E2229'}}>
                🔑 Accès au compte
              </h2>
              <button
                onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); setRevokeSessionsState(null); }}
                style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.5rem', color: '#5C636E'}}
              >
                <X size={24} />
              </button>
            </div>

            {!resetPasswordResult ? (
              <>
                <div style={{
                  padding: '1rem',
                  background: '#FFFFFF',
                  borderRadius: '10px',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{margin: '0 0 0.5rem 0', fontWeight: '600', color: '#1E2229'}}>
                    {resetPasswordUser.name || 'Utilisateur'}
                  </p>
                  <p style={{margin: '0 0 0.25rem 0', color: '#5C636E', fontSize: '0.9rem'}}>
                    📱 {resetPasswordUser.phone || 'N/A'}
                  </p>
                  {resetPasswordUser.email && (
                    <p style={{margin: 0, color: '#5C636E', fontSize: '0.9rem'}}>
                      ✉️ {resetPasswordUser.email}
                    </p>
                  )}
                </div>

                <div style={{
                  padding: '1rem',
                  background: '#fef3c7',
                  borderRadius: '10px',
                  marginBottom: '1.5rem',
                  border: '1px solid #FFB900'
                }}>
                  <p style={{margin: 0, color: '#C9350F', fontSize: '0.9rem'}}>
                    ⚠️ Un mot de passe temporaire à 8 chiffres sera généré. Communiquez-le à l'abonné pour qu'il puisse se reconnecter : il devra en choisir un nouveau à sa prochaine connexion. Toutes ses connexions ouvertes seront coupées.
                  </p>
                </div>

                <div style={{display: 'flex', gap: '1rem', justifyContent: 'flex-end'}}>
                  <button
                    onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); setRevokeSessionsState(null); }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#E9EBEF',
                      color: '#414852',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => handleResetUserPassword(resetPasswordUser.user_id)}
                    disabled={resetPasswordLoading}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: resetPasswordLoading ? '#9AA0A8' : '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: resetPasswordLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {resetPasswordLoading ? '⏳ En cours...' : '🔑 Réinitialiser'}
                  </button>
                </div>

                {/* V4 sécurité : coupure à distance des connexions, sans changer le mot de passe */}
                <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #E9EBEF' }}>
                  <p style={{ margin: '0 0 0.25rem 0', fontWeight: '700', color: '#1E2229' }}>📵 Déconnecter tous les appareils</p>
                  <p style={{ margin: '0 0 0.75rem 0', color: '#5C636E', fontSize: '0.85rem' }}>
                    Coupe toutes les connexions ouvertes de ce compte, par exemple après le vol d'un téléphone. Le mot de passe ne change pas : conseillez aussi une réinitialisation si le téléphone a été volé.
                  </p>
                  {revokeSessionsState && typeof revokeSessionsState === 'object' ? (
                    <div style={{ padding: '8px 12px', borderRadius: 8, fontWeight: 600, fontSize: '0.88rem', background: revokeSessionsState.ok ? '#ECFDF5' : '#FEF2F2', color: revokeSessionsState.ok ? '#047857' : '#B91C1C' }}>
                      {revokeSessionsState.ok ? '✅ ' : '❌ '}{revokeSessionsState.text}
                    </div>
                  ) : revokeSessionsState === 'confirm' ? (
                    <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.88rem', color: '#B91C1C', fontWeight: 600 }}>Confirmer la déconnexion de tous les appareils ?</span>
                      <button onClick={() => handleRevokeSessions(resetPasswordUser)} style={{ padding: '8px 16px', backgroundColor: '#1C1C1C', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Oui, déconnecter</button>
                      <button onClick={() => setRevokeSessionsState(null)} style={{ padding: '8px 16px', backgroundColor: '#E9EBEF', color: '#414852', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}>Annuler</button>
                    </div>
                  ) : (
                    <button onClick={() => setRevokeSessionsState('confirm')} disabled={revokeSessionsState === 'loading'}
                      style={{ padding: '9px 18px', backgroundColor: '#1C1C1C', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: revokeSessionsState === 'loading' ? 'not-allowed' : 'pointer' }}>
                      {revokeSessionsState === 'loading' ? '⏳ En cours...' : '📵 Déconnecter tous les appareils'}
                    </button>
                  )}
                </div>
              </>
            ) : resetPasswordResult.success ? (
              <>
                <div style={{
                  padding: '1.5rem',
                  background: '#ECFDF5',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  border: '1px solid #86efac',
                  textAlign: 'center'
                }}>
                  <p style={{margin: '0 0 0.5rem 0', color: '#166534', fontWeight: '600', fontSize: '1rem'}}>
                    ✅ Mot de passe réinitialisé avec succès !
                  </p>
                  <p style={{margin: '0 0 0.25rem 0', color: '#166534', fontSize: '0.9rem'}}>
                    Nouveau mot de passe temporaire :
                  </p>
                  <div style={{
                    background: '#fff',
                    border: '2px dashed #10B981',
                    borderRadius: '10px',
                    padding: '1rem',
                    margin: '1rem 0',
                    fontSize: '2rem',
                    fontWeight: '700',
                    letterSpacing: '0.3rem',
                    color: '#166534',
                    fontFamily: 'monospace'
                  }}>
                    {resetPasswordResult.temporaryPassword}
                  </div>
                  <p style={{margin: '0.5rem 0 0 0', color: '#166534', fontSize: '0.85rem'}}>
                    📱 Communiquez ce mot de passe à <strong>{resetPasswordResult.user?.name}</strong> ({resetPasswordResult.user?.phone})
                  </p>
                </div>

                <div style={{
                  padding: '0.75rem 1rem',
                  background: '#fff7ed',
                  borderRadius: '8px',
                  border: '1px solid #fdba74',
                  marginBottom: '1.5rem'
                }}>
                  <p style={{margin: 0, color: '#9a3412', fontSize: '0.85rem'}}>
                    💡 À sa prochaine connexion, l'app lui demandera de choisir un nouveau mot de passe (dès la prochaine mise à jour des applications).
                  </p>
                </div>

                <div style={{display: 'flex', justifyContent: 'center'}}>
                  <button
                    onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); setRevokeSessionsState(null); }}
                    style={{
                      padding: '10px 30px',
                      backgroundColor: '#C9350F',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{
                  padding: '1.5rem',
                  background: '#fee2e2',
                  borderRadius: '12px',
                  marginBottom: '1.5rem',
                  border: '1px solid #fca5a5',
                  textAlign: 'center'
                }}>
                  <p style={{margin: 0, color: '#991b1b', fontWeight: '600'}}>
                    ❌ {resetPasswordResult.error}
                  </p>
                </div>
                <div style={{display: 'flex', gap: '1rem', justifyContent: 'center'}}>
                  <button
                    onClick={() => setResetPasswordResult(null)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Réessayer
                  </button>
                  <button
                    onClick={() => { setShowResetPasswordModal(false); setResetPasswordResult(null); setRevokeSessionsState(null); }}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#E9EBEF',
                      color: '#414852',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Fermer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal de modification d'abonnement */}
      {showEditSubscriptionModal && editingSubscription && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }} onClick={() => setShowEditSubscriptionModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {(() => {
              return (
                <>
                  {/* Header */}
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                    <h2 style={{margin: 0, color: '#1C1C1C'}}>Vérifier l'abonnement du client</h2>
                    <button
                      onClick={() => setShowEditSubscriptionModal(false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#5C636E'
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Client Info - Éditable */}
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                  }}>
                    <h3 style={{margin: '0 0 1rem 0', color: '#1E2229', fontSize: '0.875rem', textTransform: 'uppercase', fontWeight: '600'}}>Informations du client</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                      <div>
                        <label style={{display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: '#5C636E', textTransform: 'uppercase', fontWeight: '600'}}>Nom</label>
                        <input
                          type="text"
                          value={editFormData.client_name}
                          onChange={(e) => setEditFormData({...editFormData, client_name: e.target.value})}
                          placeholder="Nom du client"
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: '2px solid #E9EBEF',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: '#1E2229',
                            background: 'white',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: '#5C636E', textTransform: 'uppercase', fontWeight: '600'}}>Email</label>
                        <input
                          type="email"
                          value={editFormData.client_email}
                          onChange={(e) => setEditFormData({...editFormData, client_email: e.target.value})}
                          placeholder={editingSubscription.user?.email ? 'Email du client' : 'Ajouter un email...'}
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: `2px solid ${!editingSubscription.user?.email && !editFormData.client_email ? '#FFB900' : '#E9EBEF'}`,
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: '#1E2229',
                            background: !editingSubscription.user?.email && !editFormData.client_email ? '#fffbeb' : 'white',
                            boxSizing: 'border-box'
                          }}
                        />
                        {!editingSubscription.user?.email && !editFormData.client_email && (
                          <p style={{margin: '0.25rem 0 0 0', fontSize: '0.7rem', color: '#FFB900', fontWeight: '500'}}>⚠️ Aucun email — ajoutez-en un pour activer la récupération de mot de passe</p>
                        )}
                      </div>
                      <div style={{gridColumn: '1 / -1'}}>
                        <label style={{display: 'block', marginBottom: '0.25rem', fontSize: '0.75rem', color: '#5C636E', textTransform: 'uppercase', fontWeight: '600'}}>Téléphone</label>
                        <input
                          type="tel"
                          value={editFormData.client_phone}
                          onChange={(e) => setEditFormData({...editFormData, client_phone: e.target.value})}
                          placeholder="Numéro de téléphone"
                          style={{
                            width: '100%',
                            padding: '0.5rem 0.75rem',
                            border: '2px solid #E9EBEF',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '500',
                            color: '#1E2229',
                            background: 'white',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Accès au compte : suspendre / bannir / réactiver (coupe toutes les connexions) */}
                  {(() => {
                    const st = editingSubscription.user?.status || 'active';
                    const badge = { active: ['Actif', '#ECFDF5', '#047857'], suspended: ['Suspendu', '#FFF7E6', '#B45309'], banned: ['Banni', '#FEE2E2', '#B91C1C'] }[st] || ['Actif', '#ECFDF5', '#047857'];
                    const choose = (status) => setAccountAction({ status, reason: '', busy: false, error: '' });
                    const submit = async () => {
                      const a = accountAction;
                      if (a.status !== 'active' && (a.reason || '').trim().length < 3) { setAccountAction({ ...a, error: 'Indiquez le motif (au moins 3 caractères).' }); return; }
                      setAccountAction({ ...a, busy: true, error: '' });
                      try {
                        const r = await apiService.request(`/admin/users/${editingSubscription.user_id}/status`, { method: 'PATCH', userType: 'admin', body: JSON.stringify({ status: a.status, reason: (a.reason || '').trim() }) });
                        if (!r?.success) throw new Error(r?.error || 'Erreur');
                        const user = { ...(editingSubscription.user || {}), status: r.status, status_reason: r.status_reason };
                        setEditingSubscription({ ...editingSubscription, user });
                        setSubscriptions((list) => (list || []).map((s) => (s.user_id === editingSubscription.user_id ? { ...s, user: { ...(s.user || {}), status: r.status, status_reason: r.status_reason } } : s)));
                        setAccountAction({ ...a, busy: false, done: r.message });
                      } catch (e) {
                        setAccountAction({ ...a, busy: false, error: e.message || 'Erreur' });
                      }
                    };
                    const btn = (bg, fg) => ({ padding: '0.55rem 0.9rem', background: bg, color: fg, border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '0.85rem' });
                    return (
                      <div style={{ border: '1px solid #E9EBEF', padding: '1.25rem', borderRadius: '12px', marginBottom: '2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.75rem' }}>
                          <h3 style={{ margin: 0, color: '#1E2229', fontSize: '0.875rem', textTransform: 'uppercase', fontWeight: '600' }}>Accès au compte</h3>
                          <span style={{ background: badge[1], color: badge[2], padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: '700' }}>{badge[0]}</span>
                        </div>
                        {st !== 'active' && editingSubscription.user?.status_reason ? (
                          <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#414852' }}>Motif : {editingSubscription.user.status_reason}</p>
                        ) : null}
                        {!accountAction || accountAction.done ? (
                          <>
                            {accountAction?.done ? <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.85rem', color: '#047857', fontWeight: '600' }}>✓ {accountAction.done}</p> : null}
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                              {st !== 'suspended' ? <button onClick={() => choose('suspended')} style={btn('#FFF7E6', '#B45309')}>Suspendre</button> : null}
                              {st !== 'banned' ? <button onClick={() => choose('banned')} style={btn('#FEE2E2', '#B91C1C')}>Bannir</button> : null}
                              {st !== 'active' ? <button onClick={() => choose('active')} style={btn('#ECFDF5', '#047857')}>Réactiver</button> : null}
                            </div>
                            <p style={{ margin: '0.6rem 0 0 0', fontSize: '0.75rem', color: '#5C636E' }}>Suspendre ou bannir coupe immédiatement toutes les connexions du client (apps et site) et bloque toute nouvelle connexion, y compris par code WhatsApp.</p>
                          </>
                        ) : (
                          <div>
                            <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.9rem', color: '#1E2229', fontWeight: '600' }}>
                              {accountAction.status === 'suspended' ? 'Suspendre ce client (temporaire, réactivable)' : accountAction.status === 'banned' ? 'Bannir ce client (définitif, réactivable seulement par un admin)' : 'Réactiver ce compte'}
                            </p>
                            {accountAction.status !== 'active' ? (
                              <textarea
                                value={accountAction.reason}
                                onChange={(e) => setAccountAction({ ...accountAction, reason: e.target.value, error: '' })}
                                placeholder="Motif (visible dans l'historique admin)"
                                rows={2}
                                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: '0.6rem', fontFamily: 'inherit' }}
                              />
                            ) : null}
                            {accountAction.error ? <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.85rem', color: '#B91C1C' }}>{accountAction.error}</p> : null}
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <button onClick={() => setAccountAction(null)} disabled={accountAction.busy} style={btn('#F4F5F7', '#1E2229')}>Annuler</button>
                              <button onClick={submit} disabled={accountAction.busy} style={btn(accountAction.status === 'banned' ? '#B91C1C' : accountAction.status === 'suspended' ? '#B45309' : '#047857', '#FFFFFF')}>
                                {accountAction.busy ? '…' : accountAction.status === 'banned' ? 'Confirmer le bannissement' : accountAction.status === 'suspended' ? 'Confirmer la suspension' : 'Confirmer la réactivation'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Formulaire */}
                  <div style={{marginBottom: '2rem'}}>
                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Plan</label>
                      <select
                        value={editFormData.plan_type}
                        onChange={(e) => {
                          const newPlanType = e.target.value;
                          const newPlanName = PLAN_DETAILS[newPlanType]?.name || newPlanType;
                          setEditFormData({
                            ...editFormData,
                            plan_type: newPlanType,
                            plan_name: newPlanName
                          });
                        }}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #E9EBEF',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: '#1E2229',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Sélectionner un plan</option>
                        <option value="basic">BASIQUE</option>
                        <option value="private-basic">PRIVÉ BASIQUE</option>
                        <option value="standard">STANDARD</option>
                        <option value="premium">PREMIUM</option>
                        <option value="university">UNIVERSITÉ</option>
                        <option value="elite">ÉLITE</option>
                      </select>
                    </div>

                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Date d'expiration</label>
                      <input
                        type="date"
                        value={editFormData.valid_until}
                        onChange={(e) => setEditFormData({...editFormData, valid_until: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #E9EBEF',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: '#1E2229'
                        }}
                      />
                    </div>

                    <div style={{marginBottom: '1.5rem'}}>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Statut</label>
                      <select
                        value={editFormData.status}
                        onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          border: '2px solid #E9EBEF',
                          borderRadius: '8px',
                          fontSize: '0.95rem',
                          fontWeight: '500',
                          color: '#1E2229',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="active">Actif</option>
                        <option value="expired">Expiré</option>
                        <option value="suspended">Suspendu</option>
                        <option value="revoked">Révoqué</option>
                      </select>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div style={{display: 'flex', gap: '1rem'}}>
                    <button
                      onClick={() => setShowEditSubscriptionModal(false)}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#F4F5F7',
                        color: '#1E2229',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#E9EBEF'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#F4F5F7'}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!editFormData.plan_type) {
                          alert('Veuillez sélectionner un plan');
                          return;
                        }
                        if (!editFormData.valid_until) {
                          alert('Veuillez sélectionner une date d\'expiration');
                          return;
                        }
                        if (!editFormData.client_name?.trim()) {
                          alert('Veuillez saisir le nom du client');
                          return;
                        }
                        if (!editFormData.client_phone?.trim()) {
                          alert('Veuillez saisir le numéro de téléphone');
                          return;
                        }
                        
                        try {
                          // 1. Mettre à jour les informations du client
                          const clientInfoChanged = 
                            editFormData.client_name !== (editingSubscription.user?.name || '') ||
                            editFormData.client_phone !== (editingSubscription.user?.phone || '') ||
                            editFormData.client_email !== (editingSubscription.user?.email || '');

                          if (clientInfoChanged) {
                            const userResponse = await apiService.request(
                              `/admin/users/${editingSubscription.user_id}`,
                              {
                                method: 'PUT',
                                userType: 'admin',
                                body: JSON.stringify({
                                  name: editFormData.client_name.trim(),
                                  phone: editFormData.client_phone.trim(),
                                  email: editFormData.client_email.trim()
                                })
                              }
                            );

                            if (!userResponse?.success) {
                              const errorMsg = userResponse?.error || 'Erreur lors de la mise à jour du client';
                              alert('❌ ' + errorMsg);
                              return;
                            }
                          }

                          // 2. Mettre à jour l'abonnement
                          const planPrice = planPrices[editFormData.plan_type]?.monthly || 0;
                          
                          // S'assurer que le status est valide (jamais 'no_subscription')
                          const validStatus = editFormData.status === 'no_subscription' ? 'active' : editFormData.status;

                          const response = await apiService.request(
                            `/subscriptions/admin/${editingSubscription.subscription_id || 'null'}`,
                            {
                              method: 'PUT',
                              userType: 'admin',
                              body: JSON.stringify({
                                user_id: editingSubscription.user_id,
                                plan_type: editFormData.plan_type,
                                plan_name: editFormData.plan_name,
                                price: planPrice,
                                valid_until: editFormData.valid_until,
                                status: validStatus
                              })
                            }
                          );

                          if (response?.success) {
                            // Mettre à jour la liste des abonnements (y compris infos client)
                            const updatedUser = {
                              ...editingSubscription.user,
                              name: editFormData.client_name.trim(),
                              phone: editFormData.client_phone.trim(),
                              email: editFormData.client_email.trim()
                            };

                            if (!editingSubscription.subscription_id) {
                              // Cas: nouvel abonnement créé
                              setSubscriptions(subscriptions.map(sub => 
                                sub.user_id === editingSubscription.user_id && !sub.subscription_id
                                  ? { ...(response.subscription || response.data), user: updatedUser }
                                  : sub
                              ));
                            } else {
                              // Cas: abonnement existant modifié
                              setSubscriptions(subscriptions.map(sub => 
                                sub.subscription_id === editingSubscription.subscription_id 
                                  ? {
                                      ...sub,
                                      plan_type: editFormData.plan_type,
                                      plan_name: editFormData.plan_name,
                                      valid_until: editFormData.valid_until,
                                      status: editFormData.status,
                                      user: updatedUser
                                    }
                                  : sub
                              ));
                            }
                            setShowEditSubscriptionModal(false);
                            setEditingSubscription(null);
                            setErrorMessage('');
                            alert('✅ ' + (clientInfoChanged ? 'Client et abonnement mis' : 'Abonnement mis') + ' à jour avec succès');
                          } else {
                            const errorMsg = response?.error || response?.message || 'Erreur serveur';
                            setErrorMessage('Erreur: ' + errorMsg);
                            alert('❌ Erreur: ' + errorMsg);
                          }
                        } catch (error) {
                          console.error('Erreur lors de la modification:', error);
                          const errorMsg = error?.error || error?.message || 'Erreur de connexion au serveur';
                          setErrorMessage('Erreur: ' + errorMsg);
                          alert('❌ Erreur: ' + errorMsg);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: '#C9350F',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.95rem',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#0256a3'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#C9350F'}
                    >
                      Enregistrer
                    </button>
                  </div>

                  {/* Zone dangereuse - Supprimer l'abonné */}
                  <div style={{
                    marginTop: '1.5rem',
                    padding: '1.25rem',
                    border: '1px solid #fecaca',
                    borderRadius: '12px',
                    background: '#fef2f2'
                  }}>
                    <h4 style={{margin: '0 0 0.75rem 0', color: '#991b1b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: '700'}}>Zone dangereuse</h4>
                    <button
                      onClick={async () => {
                        const clientName = editingSubscription.user?.name || 'cet utilisateur';
                        if (!window.confirm(`⚠️ ATTENTION: Supprimer définitivement ${clientName} ?\n\nCette action est IRRÉVERSIBLE et supprimera aussi son abonnement, ses scans, ses paiements et ses notes.`)) return;
                        if (!window.confirm(`Dernière confirmation: êtes-vous vraiment sûr de vouloir supprimer ${clientName} ?`)) return;
                        const paymentsChoice = askPaymentsChoice(clientName);
                        if (!paymentsChoice) return;

                        try {
                          const response = await apiService.request(
                            `/admin/users/${editingSubscription.user_id}?payments=${paymentsChoice}`,
                            { method: 'DELETE', userType: 'admin' }
                          );
                          if (response?.success) {
                            setSubscriptions(subscriptions.filter(s => s.user_id !== editingSubscription.user_id));
                            setShowEditSubscriptionModal(false);
                            setEditingSubscription(null);
                            alert('✅ Utilisateur supprimé définitivement');
                          } else {
                            alert('❌ ' + (response?.error || 'Erreur serveur'));
                          }
                        } catch (error) {
                          alert('❌ ' + (error?.error || error?.message || 'Erreur de connexion'));
                        }
                      }}
                      style={{
                        padding: '0.6rem 1.25rem',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
                      onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                    >
                      🗑️ Supprimer cet abonné définitivement
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal de modification du chauffeur */}
      {showEditDriverModal && editingDriver && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001
        }} onClick={() => setShowEditDriverModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '550px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
              <h2 style={{margin: 0, color: '#1C1C1C'}}>Modifier le chauffeur</h2>
              <button
                onClick={() => setShowEditDriverModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#5C636E'
                }}
              >
                ✕
              </button>
            </div>

            {/* Photo + Véhicule info */}
            <div style={{
              background: '#FFFFFF',
              padding: '1rem 1.5rem',
              borderRadius: '12px',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: '1rem'
            }}>
              {editingDriver.photo ? (
                <Img src={editingDriver.photo} alt={editingDriver.name} style={{width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover'}} />
              ) : (
                <div style={{width: '48px', height: '48px', borderRadius: '50%', background: '#C9350F', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '1.2rem'}}>
                  {editingDriver.name?.charAt(0)}
                </div>
              )}
              <div>
                <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>{editingDriver.vehicle?.brand} {editingDriver.vehicle?.model}</p>
                <p style={{margin: 0, fontSize: '0.85rem', color: '#5C636E'}}>{editingDriver.vehicle?.plate} • {editingDriver.vehicle?.color}</p>
              </div>
            </div>

            {/* Formulaire */}
            <div style={{marginBottom: '1.5rem'}}>
              <div style={{marginBottom: '1.25rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Nom complet</label>
                <input
                  type="text"
                  value={editDriverFormData.driver_name}
                  onChange={(e) => setEditDriverFormData({...editDriverFormData, driver_name: e.target.value})}
                  placeholder="Nom du chauffeur"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #E9EBEF',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    color: '#1E2229',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{marginBottom: '1.25rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Téléphone</label>
                <input
                  type="tel"
                  value={editDriverFormData.driver_phone}
                  onChange={(e) => setEditDriverFormData({...editDriverFormData, driver_phone: e.target.value})}
                  placeholder="Numéro de téléphone"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '2px solid #E9EBEF',
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    color: '#1E2229',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{marginBottom: '1.25rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Email</label>
                <input
                  type="email"
                  value={editDriverFormData.driver_email}
                  onChange={(e) => setEditDriverFormData({...editDriverFormData, driver_email: e.target.value})}
                  placeholder={editingDriver.email ? 'Email du chauffeur' : 'Ajouter un email...'}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `2px solid ${!editingDriver.email && !editDriverFormData.driver_email ? '#FFB900' : '#E9EBEF'}`,
                    borderRadius: '8px',
                    fontSize: '0.95rem',
                    fontWeight: '500',
                    color: '#1E2229',
                    background: !editingDriver.email && !editDriverFormData.driver_email ? '#fffbeb' : 'white',
                    boxSizing: 'border-box'
                  }}
                />
                {!editingDriver.email && !editDriverFormData.driver_email && (
                  <p style={{margin: '0.25rem 0 0 0', fontSize: '0.7rem', color: '#FFB900', fontWeight: '500'}}>⚠️ Aucun email — ajoutez-en un pour activer la récupération de mot de passe</p>
                )}
              </div>
              <div style={{marginBottom: '1.25rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Numéro de permis de conduire</label>
                <input
                  type="text"
                  value={editDriverFormData.license_number}
                  onChange={(e) => setEditDriverFormData({...editDriverFormData, license_number: e.target.value})}
                  placeholder="Facultatif"
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', background: 'white', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{marginBottom: '1.25rem'}}>
                <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Numéro de carte d'identité</label>
                <input
                  type="text"
                  value={editDriverFormData.id_number}
                  onChange={(e) => setEditDriverFormData({...editDriverFormData, id_number: e.target.value})}
                  placeholder="Facultatif"
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', background: 'white', boxSizing: 'border-box' }}
                />
              </div>
            </div>

            {/* Informations véhicule */}
            <div style={{marginBottom: '1.5rem'}}>
              <h3 style={{margin: '0 0 1rem', fontSize: '1rem', color: '#1E2229', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                🚗 Véhicule
              </h3>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                <div>
                  <label style={{display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Marque</label>
                  <input
                    type="text"
                    value={editDriverFormData.vehicle_brand}
                    onChange={(e) => setEditDriverFormData({...editDriverFormData, vehicle_brand: e.target.value})}
                    placeholder="Toyota"
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', boxSizing: 'border-box'}}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Modèle</label>
                  <input
                    type="text"
                    value={editDriverFormData.vehicle_model}
                    onChange={(e) => setEditDriverFormData({...editDriverFormData, vehicle_model: e.target.value})}
                    placeholder="Corolla"
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', boxSizing: 'border-box'}}
                  />
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Couleur</label>
                  <select
                    value={VEHICLE_COLORS.some(c => c.code === editDriverFormData.vehicle_color) ? editDriverFormData.vehicle_color : ''}
                    onChange={(e) => setEditDriverFormData({...editDriverFormData, vehicle_color: e.target.value})}
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', boxSizing: 'border-box', background: '#fff'}}
                  >
                    <option value="">— Choisir —{editDriverFormData.vehicle_color && !VEHICLE_COLORS.some(c => c.code === editDriverFormData.vehicle_color) ? ` (saisie actuelle : ${editDriverFormData.vehicle_color})` : ''}</option>
                    {VEHICLE_COLORS.map(c => <option key={c.code} value={c.code}>{c.fr}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Plaque</label>
                  <input
                    type="text"
                    value={editDriverFormData.vehicle_plate}
                    onChange={(e) => setEditDriverFormData({...editDriverFormData, vehicle_plate: e.target.value.toUpperCase()})}
                    placeholder="AD3839KI"
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', boxSizing: 'border-box', textTransform: 'uppercase'}}
                  />
                </div>
                <div style={{gridColumn: '1 / -1'}}>
                  <label style={{display: 'block', marginBottom: '0.4rem', fontWeight: '600', color: '#1E2229', fontSize: '0.875rem'}}>Année</label>
                  <input
                    type="number"
                    value={editDriverFormData.vehicle_year}
                    onChange={(e) => setEditDriverFormData({...editDriverFormData, vehicle_year: e.target.value})}
                    placeholder="2019"
                    min="1990"
                    max="2030"
                    style={{width: '100%', padding: '0.75rem', border: '2px solid #E9EBEF', borderRadius: '8px', fontSize: '0.95rem', fontWeight: '500', color: '#1E2229', boxSizing: 'border-box'}}
                  />
                </div>
              </div>
            </div>

            {/* Catégories du chauffeur */}
            <div style={{marginBottom: '1.5rem'}}>
              <h3 style={{margin: '0 0 0.75rem', fontSize: '1rem', color: '#1E2229', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                🏷️ Catégories de courses
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#5C636E', marginBottom: '10px', marginTop: 0 }}>Le chauffeur recevra les courses correspondant à ses catégories</p>
              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                {[
                  { key: 'standard', label: '🚖 Standard', desc: 'Covoiturage & Mbiyo', bg: '#d1fae5', color: '#059669', border: '#10b981' },
                  { key: 'confort', label: '💺 Confort', desc: '+30%', bg: '#FEE9E2', color: '#C9350F', border: '#FF7800' },
                  { key: 'premium', label: '⭐ Premium', desc: '+50%', bg: '#fef3c7', color: '#C9350F', border: '#FFB900' },
                  { key: 'moto', label: '🏍️ Moto', desc: 'Économique', bg: '#fff7ed', color: '#c2410c', border: '#f97316' }
                ].map(cat => {
                  const isSelected = (editDriverFormData.driver_categories || []).includes(cat.key);
                  return (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => {
                        const cats = editDriverFormData.driver_categories || [];
                        let newCats;
                        if (isSelected) {
                          if (cats.length === 1) return; // au moins une catégorie
                          newCats = cats.filter(c => c !== cat.key);
                        } else {
                          newCats = fillCategoryGaps([...cats, cat.key]);
                        }
                        setEditDriverFormData({...editDriverFormData, driver_categories: newCats});
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '12px',
                        border: `2px solid ${isSelected ? cat.border : '#E9EBEF'}`,
                        background: isSelected ? cat.bg : '#FFFFFF',
                        color: isSelected ? cat.color : '#9AA0A8',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        transition: 'all 0.2s',
                        flex: '1',
                        minWidth: '90px',
                        textAlign: 'center'
                      }}
                    >
                      {cat.label}
                      <div style={{ fontSize: '0.7rem', fontWeight: '400', opacity: 0.8 }}>{cat.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Type de chauffeur (rôles) */}
            <div style={{marginBottom: '1.5rem'}}>
              <h3 style={{margin: '0 0 0.75rem', fontSize: '1rem', color: '#1E2229', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                👤 Type de chauffeur
              </h3>
              <p style={{ fontSize: '0.8rem', color: '#5C636E', marginBottom: '10px', marginTop: 0 }}>Définir si ce chauffeur fait du transport, de la livraison, ou les deux</p>
              <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap'}}>
                {[
                  { key: 'driver', label: '🚗 Chauffeur', desc: 'Transport de personnes', bg: '#FEE9E2', color: '#C9350F', border: '#FF7800' },
                  { key: 'deliverer', label: '📦 Livreur', desc: 'Livraison de colis', bg: '#fef3c7', color: '#C9350F', border: '#FFB900' }
                ].map(role => {
                  const isSelected = (editDriverFormData.approved_roles || []).includes(role.key);
                  return (
                    <button
                      key={role.key}
                      type="button"
                      onClick={() => {
                        const roles = editDriverFormData.approved_roles || [];
                        let newRoles;
                        if (isSelected) {
                          if (roles.length === 1) return; // au moins un rôle
                          newRoles = roles.filter(r => r !== role.key);
                        } else {
                          newRoles = [...roles, role.key];
                        }
                        setEditDriverFormData({...editDriverFormData, approved_roles: newRoles});
                      }}
                      style={{
                        padding: '10px 16px',
                        borderRadius: '12px',
                        border: `2px solid ${isSelected ? role.border : '#E9EBEF'}`,
                        background: isSelected ? role.bg : '#FFFFFF',
                        color: isSelected ? role.color : '#9AA0A8',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '0.85rem',
                        transition: 'all 0.2s',
                        flex: '1',
                        minWidth: '120px',
                        textAlign: 'center'
                      }}
                    >
                      {role.label}
                      <div style={{ fontSize: '0.7rem', fontWeight: '400', opacity: 0.8 }}>{role.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Buttons */}
            <div style={{display: 'flex', gap: '1rem'}}>
              <button
                onClick={() => setShowEditDriverModal(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#F4F5F7',
                  color: '#1E2229',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#E9EBEF'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#F4F5F7'}
              >
                Annuler
              </button>
              <button
                onClick={async () => {
                  if (!editDriverFormData.driver_name?.trim()) {
                    alert('Veuillez saisir le nom du chauffeur');
                    return;
                  }
                  if (!editDriverFormData.driver_phone?.trim()) {
                    alert('Veuillez saisir le numéro de téléphone');
                    return;
                  }

                  try {
                    const response = await apiService.request(
                      `/admin/drivers/${editingDriver.driver_id}`,
                      {
                        method: 'PUT',
                        userType: 'admin',
                        body: JSON.stringify({
                          name: editDriverFormData.driver_name.trim(),
                          phone: editDriverFormData.driver_phone.trim(),
                          email: editDriverFormData.driver_email.trim(),
                          license_number: (editDriverFormData.license_number || '').trim(),
                          id_number: (editDriverFormData.id_number || '').trim(),
                          categories: editDriverFormData.driver_categories || ['standard'],
                          approved_roles: editDriverFormData.approved_roles || ['driver'],
                          'vehicle.brand': editDriverFormData.vehicle_brand.trim(),
                          'vehicle.model': editDriverFormData.vehicle_model.trim(),
                          'vehicle.color': editDriverFormData.vehicle_color.trim(),
                          'vehicle.plate': editDriverFormData.vehicle_plate.trim().toUpperCase(),
                          'vehicle.year': editDriverFormData.vehicle_year ? parseInt(editDriverFormData.vehicle_year) : undefined
                        })
                      }
                    );

                    if (response?.success) {
                      setDrivers(drivers.map(d => 
                        d.driver_id === editingDriver.driver_id
                          ? {
                              ...d,
                              name: editDriverFormData.driver_name.trim(),
                              phone: editDriverFormData.driver_phone.trim(),
                              email: editDriverFormData.driver_email.trim(),
                              categories: editDriverFormData.driver_categories || ['standard'],
                              approved_roles: editDriverFormData.approved_roles || ['driver'],
                              vehicle: {
                                ...d.vehicle,
                                brand: editDriverFormData.vehicle_brand.trim(),
                                model: editDriverFormData.vehicle_model.trim(),
                                color: editDriverFormData.vehicle_color.trim(),
                                plate: editDriverFormData.vehicle_plate.trim().toUpperCase(),
                                year: editDriverFormData.vehicle_year ? parseInt(editDriverFormData.vehicle_year) : d.vehicle?.year
                              }
                            }
                          : d
                      ));
                      setShowEditDriverModal(false);
                      setEditingDriver(null);
                      alert('✅ Informations du chauffeur mises à jour avec succès');
                    } else {
                      const errorMsg = response?.error || 'Erreur serveur';
                      alert('❌ ' + errorMsg);
                    }
                  } catch (error) {
                    console.error('Erreur modification chauffeur:', error);
                    const errorMsg = error?.error || error?.message || 'Erreur de connexion au serveur';
                    alert('❌ ' + errorMsg);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: '#C9350F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  transition: 'background 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#0256a3'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#C9350F'}
              >
                Enregistrer
              </button>
            </div>

            {/* Zone dangereuse - Invalider / Supprimer */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1.25rem',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              background: '#fef2f2'
            }}>
              <h4 style={{margin: '0 0 0.75rem 0', color: '#991b1b', fontSize: '0.8rem', textTransform: 'uppercase', fontWeight: '700'}}>Zone dangereuse</h4>
              <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap'}}>
                {editingDriver.status === 'approved' && (
                  <button
                    onClick={async () => {
                      const reason = prompt('Raison de la suspension (optionnel):');
                      if (reason === null) return; // Annulé
                      
                      if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir suspendre le chauffeur ${editingDriver.name} ?`)) return;

                      try {
                        const response = await apiService.request(
                          `/admin/drivers/${editingDriver.driver_id}/suspend`,
                          {
                            method: 'POST',
                            userType: 'admin',
                            body: JSON.stringify({ reason: reason || '' })
                          }
                        );
                        if (response?.success) {
                          setDrivers(drivers.map(d => 
                            d.driver_id === editingDriver.driver_id ? { ...d, status: 'suspended' } : d
                          ));
                          setShowEditDriverModal(false);
                          setEditingDriver(null);
                          alert('✅ Chauffeur suspendu avec succès');
                        } else {
                          alert('❌ ' + (response?.error || 'Erreur serveur'));
                        }
                      } catch (error) {
                        alert('❌ ' + (error?.error || error?.message || 'Erreur de connexion'));
                      }
                    }}
                    style={{
                      padding: '0.6rem 1.25rem',
                      background: '#FFB900',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#FF9B00'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#FFB900'}
                  >
                    🚫 Suspendre
                  </button>
                )}
                {editingDriver.status === 'suspended' && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Réactiver le chauffeur ${editingDriver.name} ?`)) return;

                      try {
                        const response = await apiService.request(
                          `/admin/drivers/${editingDriver.driver_id}/approve`,
                          { method: 'POST', userType: 'admin' }
                        );
                        if (response?.success) {
                          setDrivers(drivers.map(d => 
                            d.driver_id === editingDriver.driver_id ? { ...d, status: 'approved' } : d
                          ));
                          setShowEditDriverModal(false);
                          setEditingDriver(null);
                          alert('✅ Chauffeur réactivé avec succès');
                        } else {
                          alert('❌ ' + (response?.error || 'Erreur serveur'));
                        }
                      } catch (error) {
                        alert('❌ ' + (error?.error || error?.message || 'Erreur de connexion'));
                      }
                    }}
                    style={{
                      padding: '0.6rem 1.25rem',
                      background: '#10B981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#10B981'}
                  >
                    ✅ Réactiver
                  </button>
                )}
                <button
                  onClick={async () => {
                    if (!window.confirm(`⚠️ ATTENTION: Supprimer définitivement le chauffeur ${editingDriver.name} ?\n\nCette action est IRRÉVERSIBLE et supprimera aussi ses scans, notes et trajets.`)) return;
                    if (!window.confirm(`Dernière confirmation: êtes-vous vraiment sûr de vouloir supprimer ${editingDriver.name} ?`)) return;
                    const paymentsChoice = askPaymentsChoice(editingDriver.name);
                    if (!paymentsChoice) return;

                    try {
                      const response = await apiService.request(
                        `/admin/drivers/${editingDriver.driver_id}?payments=${paymentsChoice}`,
                        { method: 'DELETE', userType: 'admin' }
                      );
                      if (response?.success) {
                        setDrivers(drivers.filter(d => d.driver_id !== editingDriver.driver_id));
                        setShowEditDriverModal(false);
                        setEditingDriver(null);
                        alert('✅ Chauffeur supprimé définitivement');
                      } else {
                        alert('❌ ' + (response?.error || 'Erreur serveur'));
                      }
                    } catch (error) {
                      alert('❌ ' + (error?.error || error?.message || 'Erreur de connexion'));
                    }
                  }}
                  style={{
                    padding: '0.6rem 1.25rem',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    transition: 'background 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#B91C1C'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#dc2626'}
                >
                  🗑️ Supprimer définitivement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {selectedSubscription && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {(() => {
              const planKey = (selectedSubscription.plan_type || selectedSubscription.plan_name || '').toLowerCase();
              const plan = PLAN_DETAILS[planKey] || {
                name: selectedSubscription.plan_name || 'INCONNU',
                description: '',
                icon: CreditCard,
                color: '#5C636E',
                features: []
              };
              const Icon = plan.icon;

              return (
                <>
                  {/* Header */}
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '2rem'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '1rem', flex: 1}}>
                      <div style={{
                        background: `${plan.color}20`,
                        color: plan.color,
                        padding: '1rem',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Icon size={32} />
                      </div>
                      <div>
                        <h2 style={{margin: 0, color: plan.color}}>{plan.name}</h2>
                        <p style={{margin: '0.5rem 0 0 0', color: '#5C636E'}}>{plan.description}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedSubscription(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#5C636E'
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Client Info */}
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{margin: '0 0 1rem 0', color: '#1E2229'}}>Informations du client</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem'}}>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Nom</p>
                        <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>{selectedSubscription.user?.name || '-'}</p>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Téléphone</p>
                        <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>{selectedSubscription.user?.phone || '-'}</p>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Email</p>
                        <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>{selectedSubscription.user?.email || '-'}</p>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Connexion</p>
                        <div style={{margin: 0}}>{authMethodBadges(selectedSubscription.user)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Plan Details */}
                  <div style={{marginBottom: '1.5rem'}}>
                    <h3 style={{margin: '0 0 1rem 0', color: '#1E2229'}}>Détails du plan</h3>
                    <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem'}}>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Montant</p>
                        <p style={{margin: 0, fontWeight: '700', fontSize: '1.5rem', color: plan.color}}>{selectedSubscription.price ? `${selectedSubscription.price.toLocaleString()} FC` : '-'}</p>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Statut</p>
                        <span style={{
                          background: selectedSubscription.status === 'active' ? '#ECFDF5' : '#fee2e2',
                          color: selectedSubscription.status === 'active' ? '#166534' : '#991b1b',
                          padding: '0.5rem 1rem',
                          borderRadius: '6px',
                          fontWeight: '600'
                        }}>
                          {selectedSubscription.status === 'active' ? '✅ Actif' : '⏱️ Expiré'}
                        </span>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Date d\'activation</p>
                        <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>
                          {selectedSubscription.valid_from ? new Date(selectedSubscription.valid_from).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                      <div>
                        <p style={{margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#5C636E'}}>Date d\'expiration</p>
                        <p style={{margin: 0, fontWeight: '600', color: '#1E2229'}}>
                          {selectedSubscription.valid_until ? new Date(selectedSubscription.valid_until).toLocaleDateString('fr-FR') : '-'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div style={{
                    background: '#FFFFFF',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    marginBottom: '1.5rem'
                  }}>
                    <h3 style={{margin: '0 0 1rem 0', color: '#1E2229'}}>Caractéristiques incluses</h3>
                    <ul style={{margin: 0, paddingLeft: 0, listStyle: 'none'}}>
                      {plan.features.map((feature, idx) => (
                        <li key={idx} style={{
                          margin: '0.75rem 0',
                          fontSize: '0.95rem',
                          color: '#414852',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem'
                        }}>
                          <span style={{
                            background: plan.color,
                            color: 'white',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.875rem',
                            flexShrink: 0
                          }}>✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={() => setSelectedSubscription(null)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      background: plan.color,
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '1rem'
                    }}
                  >
                    Fermer
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {selectedDriver && (
        <Stable render={DriverDetailsModal} 
          key={selectedDriver.driver_id}
          driver={selectedDriver}
          onOpenDriver={(d) => setSelectedDriver(d)}
          onClose={() => setSelectedDriver(null)}
          onApprove={approveDriver}
          onReject={rejectDriver}
          onSuspend={suspendDriver}
          onReactivate={reactivateDriver}
        />
      )}

      {selectedClient && (
        <Stable render={ClientDetailsModal} key="ClientDetailsModal"
          subscription={selectedClient}
          initialEdit={selectedClientEdit}
          onClose={() => { setSelectedClient(null); setSelectedClientEdit(false); }}
          onViewTrips={(userId, clientName) => {
            setViewingClientUserId(userId);
            setViewingClientName(clientName);
            setViewingClientTrips(true);
          }}
        />
      )}
    </div>
    </AdminContext.Provider>
  );
}